// ============================================================
// HIRESPOT GY — SKILLED TRADES MARKETPLACE
// Renamed from ServicesPage → SkilledTrades
// Changes vs original:
//   • Rebranded from "Services" → "Skilled Trades"
//   • Added ProviderAuth — sign up / log in for tradespeople
//   • Added ProviderDashboard — manage listing, see reviews, renew
//   • Shared Supabase client (import from App.jsx in production)
//   • Deduped Toast (uses App.jsx toast in production)
//   • service_providers table gains: email, password_hash columns (see SQL)
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./App"; // single shared client — never create a second one

const ANNUAL_FEE_GYD  = 15000;
const ADMIN_WHATSAPP  = "5921234567"; // ← replace with real admin number

// ── Trade categories ─────────────────────────────────────────
// Premium SVG trade icons — no emojis
const TradeIcon = ({ id, size = 22, color = "currentColor" }) => {
  const icons = {
    plumbing: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8H6a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2z"/><path d="M10 22H6a2 2 0 0 1-2-2v-3"/><path d="M18 22h-4"/></svg>,
    electrical: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    carpentry: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
    painting: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18.37 2.63 14 7l-1.59-1.59a2 2 0 0 0-2.82 0L8 7l9 9 1.59-1.59a2 2 0 0 0 0-2.82L17 10l4.37-4.37a2.12 2.12 0 1 0-3-3z"/><path d="M9 8c-2 3-4 3.5-7 4l8 10c2-1 6-5 6-7"/><path d="M14.5 17.5 4.5 15"/></svg>,
    ac_refrigeration: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
    masonry: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
    landscaping: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22V12"/><path d="M5 12H2a10 10 0 0 0 20 0h-3"/><path d="M8 6.1A6 6 0 0 1 18.4 10H20a2 2 0 0 1 0 4H4a2 2 0 0 1 0-4h.5A6 6 0 0 1 8 6z"/></svg>,
    auto_mechanics: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="9"/><line x1="12" y1="15" x2="12" y2="22"/><line x1="2" y1="12" x2="9" y2="12"/><line x1="15" y1="12" x2="22" y2="12"/></svg>,
    welding: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>,
    cleaning: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 2 1-2 3.5-1.5.5v2.5l1.5.5 2 3.5-2 1"/><path d="m15 6-2 1 2 3.5 1.5.5v2.5l-1.5.5-2 3.5 2 1"/><path d="M12 2v2M12 20v2M6 8H4M20 8h-2M6 16H4M20 16h-2"/></svg>,
    security: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>,
    handyman: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  };
  return icons[id] || icons.handyman;
};

const TRADES = [
  { id: "plumbing",         label: "Plumbing"            },
  { id: "electrical",       label: "Electrical"           },
  { id: "carpentry",        label: "Carpentry & Joinery"  },
  { id: "painting",         label: "Painting & Finishing" },
  { id: "ac_refrigeration", label: "AC & Refrigeration"   },
  { id: "masonry",          label: "Masonry & Tiling"     },
  { id: "landscaping",      label: "Landscaping"          },
  { id: "auto_mechanics",   label: "Auto Mechanics"       },
  { id: "welding",          label: "Welding & Fabrication"},
  { id: "cleaning",         label: "Cleaning Services"    },
  { id: "security",         label: "Security & CCTV"      },
  { id: "handyman",         label: "General Handyman"     },
];

const REGIONS = [
  "Georgetown", "East Bank Demerara", "East Coast Demerara",
  "West Coast Demerara", "West Bank Demerara", "Berbice",
  "Essequibo", "Linden / Region 10", "Other",
];

// Common villages/communities for quick-select
const VILLAGES_BY_REGION = {
  "Georgetown": ["Queenstown", "Lacytown", "Wortmanville", "Albouystown", "Charlestown", "Kitty", "Campbellville", "Bourda", "Newtown"],
  "East Bank Demerara": ["Eccles", "Providence", "Diamond", "Grove", "Herstelling", "MDC", "Soesdyke", "Timehri"],
  "East Coast Demerara": ["Montrose", "Buxton", "Friendship", "Annandale", "Non Pareil", "Enmore", "Golden Grove", "Mahaica"],
  "West Coast Demerara": ["Vreed-en-Hoop", "Uitvlugt", "Hydronie", "Leonora", "Wales", "Versailles", "Parika"],
  "West Bank Demerara": ["Vreed-en-Hoop", "La Grange", "Uitvlugt", "Windsor Forest", "Leonora"],
  "Berbice": ["New Amsterdam", "Corriverton", "Skeldon", "Rose Hall", "Port Mourant", "Canje"],
  "Essequibo": ["Anna Regina", "Suddie", "Charity", "Bartica", "Parika"],
  "Linden / Region 10": ["Linden", "Wismar", "Mackenzie", "Christianburg"],
  "Other": [],
};

// ── Region centre coordinates (for map auto-centering) ───────
const REGION_COORDS = {
  "Georgetown":           { lat: 6.8013,  lng: -58.1551 },
  "East Bank Demerara":   { lat: 6.6800,  lng: -58.1500 },
  "East Coast Demerara":  { lat: 6.8300,  lng: -57.9800 },
  "West Coast Demerara":  { lat: 6.7000,  lng: -58.2500 },
  "West Bank Demerara":   { lat: 6.7200,  lng: -58.2000 },
  "Berbice":              { lat: 6.2500,  lng: -57.5200 },
  "Essequibo":            { lat: 6.9800,  lng: -58.5500 },
  "Linden / Region 10":   { lat: 6.0000,  lng: -58.3000 },
  "Other":                { lat: 6.8013,  lng: -58.1551 },
};

// ── Leaflet map pin picker (loads Leaflet from CDN) ───────────
const LeafletMapPicker = ({ region, lat, lng, onChange }) => {
  const mapRef    = useRef(null);
  const leafletRef = useRef(null);
  const markerRef = useRef(null);
  const mapId     = useRef("lmap_" + Math.random().toString(36).slice(2));

  useEffect(() => {
    // Load Leaflet CSS once
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id   = "leaflet-css";
      link.rel  = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    const init = () => {
      if (leafletRef.current) return; // already initialised
      const L = window.L;
      if (!L) return;

      const centre = (region && REGION_COORDS[region]) || REGION_COORDS["Georgetown"];
      const startLat = lat || centre.lat;
      const startLng = lng || centre.lng;

      const map = L.map(mapId.current, {
        center: [startLat, startLng],
        zoom: lat ? 15 : 13,
        zoomControl: true,
        attributionControl: false,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
      }).addTo(map);

      // Custom gold pin icon
      const icon = L.divIcon({
        className: "",
        html: `<div style="width:28px;height:28px;background:#C9A84C;border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 3px 12px rgba(0,0,0,0.4)"></div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 28],
      });

      const marker = L.marker([startLat, startLng], { icon, draggable: true }).addTo(map);
      markerRef.current = marker;
      leafletRef.current = map;

      marker.on("dragend", () => {
        const pos = marker.getLatLng();
        onChange(pos.lat, pos.lng);
      });

      map.on("click", (e) => {
        marker.setLatLng(e.latlng);
        onChange(e.latlng.lat, e.latlng.lng);
      });
    };

    if (window.L) {
      init();
    } else {
      const script = document.createElement("script");
      script.src  = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = init;
      document.head.appendChild(script);
    }

    return () => {
      if (leafletRef.current) {
        leafletRef.current.remove();
        leafletRef.current = null;
        markerRef.current  = null;
      }
    };
  }, []); // only mount once

  // Re-center when region changes (but don't re-init)
  useEffect(() => {
    if (!leafletRef.current || lat) return; // don't override a user-set pin
    const centre = REGION_COORDS[region] || REGION_COORDS["Georgetown"];
    leafletRef.current.setView([centre.lat, centre.lng], 13);
    markerRef.current?.setLatLng([centre.lat, centre.lng]);
  }, [region]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={labelStyle}>
        Pin Your Location
        <span style={{ color: "rgba(250,248,244,0.28)", fontWeight: 400, textTransform: "none", letterSpacing: 0, marginLeft: 6 }}>(optional · tap map or drag pin)</span>
      </label>
      <div style={{ borderRadius: 14, overflow: "hidden", border: "1px solid rgba(201,168,76,0.25)", height: 200, position: "relative", isolation: "isolate" }}>
        <div id={mapId.current} ref={mapRef} style={{ width: "100%", height: "100%" }} />
        {!lat && (
          <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", background: "rgba(14,13,11,0.82)", borderRadius: 8, padding: "5px 12px", fontSize: 11, color: "rgba(250,248,244,0.55)", pointerEvents: "none", whiteSpace: "nowrap", zIndex: 1000 }}>
            Tap to drop your pin
          </div>
        )}
        {lat && (
          <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", background: "rgba(201,168,76,0.15)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 8, padding: "5px 12px", fontSize: 11, color: "#C9A84C", fontWeight: 600, pointerEvents: "none", whiteSpace: "nowrap", zIndex: 1000 }}>
            📍 {lat.toFixed(5)}, {lng.toFixed(5)}
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: "rgba(250,248,244,0.28)" }}>Helps clients see exactly where you're based. You can drag the pin to fine-tune.</div>
    </div>
  );
};

// ── Small static map chip for ProviderModal ───────────────────
const ProviderMapChip = ({ lat, lng, name }) => {
  const mapId = useRef("pmap_" + Math.random().toString(36).slice(2));
  const leafletRef = useRef(null);

  useEffect(() => {
    if (!lat || !lng) return;
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id   = "leaflet-css";
      link.rel  = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    const init = () => {
      if (leafletRef.current) return;
      const L = window.L;
      if (!L) return;
      const map = L.map(mapId.current, {
        center: [lat, lng], zoom: 15,
        zoomControl: false, attributionControl: false,
        dragging: false, scrollWheelZoom: false,
        doubleClickZoom: false, touchZoom: false,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
      const icon = L.divIcon({
        className: "",
        html: `<div style="width:22px;height:22px;background:#C9A84C;border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 8px rgba(0,0,0,0.4)"></div>`,
        iconSize: [22, 22], iconAnchor: [11, 22],
      });
      L.marker([lat, lng], { icon }).addTo(map);
      leafletRef.current = map;
    };
    if (window.L) { init(); }
    else {
      const existing = document.querySelector('script[src*="leaflet.js"]');
      if (existing) { existing.addEventListener("load", init); }
      else {
        const script = document.createElement("script");
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
        script.onload = init;
        document.head.appendChild(script);
      }
    }
    return () => { if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; } };
  }, [lat, lng]);

  if (!lat || !lng) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(250,248,244,0.28)", textTransform: "uppercase", letterSpacing: "0.09em", marginBottom: 8 }}>Location</div>
      <div style={{ borderRadius: 14, overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)", height: 150, position: "relative" }}>
        <div id={mapId.current} style={{ width: "100%", height: "100%" }} />
        <a
          href={`https://www.google.com/maps?q=${lat},${lng}`}
          target="_blank" rel="noopener noreferrer"
          style={{ position: "absolute", bottom: 8, right: 8, background: "rgba(14,13,11,0.85)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "5px 10px", fontSize: 11, color: "#C9A84C", fontWeight: 600, textDecoration: "none", zIndex: 1000 }}>
          Open in Maps ↗
        </a>
      </div>
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────
const Stars = ({ rating = 0, size = 14, interactive = false, onChange }) => (
  <span style={{ display: "inline-flex", gap: 2 }}>
    {[1,2,3,4,5].map(n => (
      <svg key={n} width={size} height={size} viewBox="0 0 24 24"
        fill={n <= Math.round(rating) ? "#C9A84C" : "none"}
        stroke={n <= Math.round(rating) ? "#C9A84C" : "#4A4540"}
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
        style={{ cursor: interactive ? "pointer" : "default", filter: n <= Math.round(rating) ? "drop-shadow(0 0 3px rgba(201,168,76,0.4))" : "none", transition: "all 0.15s" }}
        onClick={() => interactive && onChange?.(n)}>
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ))}
  </span>
);

const openWA = (phone, msg = "") => {
  const n = phone.replace(/\D/g, "");
  window.open(`https://wa.me/${n}?text=${encodeURIComponent(msg)}`, "_blank");
};

// ── Self-contained toast (dedup with App.jsx toast in production) ──
let _svcToast = null;
const SvcToast = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    _svcToast = (msg, type = "info") => {
      const id = Date.now();
      setList(l => [...l, { id, msg, type }]);
      setTimeout(() => setList(l => l.filter(x => x.id !== id)), 3500);
    };
  }, []);
  const clr = { success: "#2D9E4E", error: "#D93025", info: "#B8922A" };
  return (
    <div style={{ position: "fixed", bottom: 88, right: 20, zIndex: 300, display: "flex", flexDirection: "column", gap: 8 }}>
      {list.map(t => (
        <div key={t.id} style={{ background: "#1C1917", border: `1px solid ${clr[t.type]}40`, borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#FAF8F4", boxShadow: "0 8px 32px rgba(0,0,0,0.5)", animation: "fadeUp 0.3s ease", display: "flex", alignItems: "center", gap: 8, minWidth: 260 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: clr[t.type], flexShrink: 0 }} />
          {t.msg}
        </div>
      ))}
    </div>
  );
};
const svcToast = (msg, type) => _svcToast?.(msg, type);

// ── Shared input style ────────────────────────────────────────
const inputStyle = {
  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10,
  padding: "13px 16px", color: "#FAF8F4", fontSize: 15, outline: "none",
  fontFamily: "DM Sans,sans-serif", width: "100%", colorScheme: "dark",
};
const labelStyle = {
  fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.5)",
  textTransform: "uppercase", letterSpacing: "0.05em",
};
const Field = ({ label, required, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label style={labelStyle}>{label}{required && <span style={{ color: "#D93025", marginLeft: 3 }}>*</span>}</label>
    {children}
  </div>
);

// ============================================================
// PHOTO UPLOADER — Supabase Storage, 2 MB limit, 1 photo/provider
// ============================================================
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB

const PhotoUploader = ({ currentUrl, providerId, onUploaded, label = "Profile Photo" }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview]     = useState(currentUrl || null);
  const [error, setError]         = useState("");
  const inputRef                  = useRef(null);

  const handleFile = async (file) => {
    setError("");
    if (!file) return;

    // Type check
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file (JPG, PNG, WEBP)."); return;
    }
    // Size check
    if (file.size > MAX_BYTES) {
      setError(`Image must be under 2 MB. Yours is ${(file.size / 1024 / 1024).toFixed(1)} MB.`); return;
    }

    setUploading(true);

    // Build a stable per-provider filename so each provider ever has exactly 1 file
    const ext      = file.name.split(".").pop().toLowerCase();
    const filename = providerId
      ? `provider_${providerId}.${ext}`
      : `tmp_${Date.now()}.${ext}`;

    // If provider already has a photo, delete it first (keeps bucket tidy)
    if (currentUrl) {
      const oldPath = currentUrl.split("/provider-photos/")[1];
      if (oldPath) {
        await supabase.storage.from("provider-photos").remove([oldPath]);
      }
    }

    const { error: upErr } = await supabase.storage
      .from("provider-photos")
      .upload(filename, file, { upsert: true, contentType: file.type });

    if (upErr) {
      setError("Upload failed. Please try again."); setUploading(false); return;
    }

    const { data } = supabase.storage.from("provider-photos").getPublicUrl(filename);
    const url = data.publicUrl;

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(file);

    setUploading(false);
    onUploaded(url);
    svcToast("Photo uploaded!", "success");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <label style={labelStyle}>{label} <span style={{ color: "rgba(250,248,244,0.28)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional · max 2 MB)</span></label>

      {/* Drop zone / preview */}
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        style={{
          border: `2px dashed ${error ? "rgba(217,48,37,0.5)" : "rgba(201,168,76,0.25)"}`,
          borderRadius: 14,
          background: "rgba(255,255,255,0.02)",
          cursor: uploading ? "not-allowed" : "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 110,
          transition: "border-color 0.2s, background 0.2s",
          position: "relative",
          overflow: "hidden",
        }}
        onMouseEnter={e => { if (!uploading) e.currentTarget.style.background = "rgba(201,168,76,0.04)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
      >
        {preview ? (
          <>
            <img
              src={preview}
              alt="Profile preview"
              style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 12 }}
            />
            {/* Hover overlay */}
            <div style={{
              position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              opacity: 0, transition: "opacity 0.2s", borderRadius: 12,
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => e.currentTarget.style.opacity = 0}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <span style={{ fontSize: 12, color: "#C9A84C", fontWeight: 700, marginTop: 6 }}>Change photo</span>
            </div>
          </>
        ) : uploading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: 20 }}>
            <div style={{ width: 32, height: 32, border: "3px solid rgba(201,168,76,0.2)", borderTopColor: "#C9A84C", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <span style={{ fontSize: 13, color: "rgba(250,248,244,0.45)" }}>Uploading…</span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: 20 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(201,168,76,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            </div>
            <span style={{ fontSize: 13, color: "rgba(250,248,244,0.45)", textAlign: "center", lineHeight: 1.5 }}>
              Tap to upload a photo<br />
              <span style={{ fontSize: 11, color: "rgba(250,248,244,0.25)" }}>JPG, PNG or WEBP · max 2 MB</span>
            </span>
          </div>
        )}
      </div>

      {error && (
        <div style={{ fontSize: 12, color: "#D93025", display: "flex", alignItems: "center", gap: 5 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {error}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={e => handleFile(e.target.files?.[0])}
      />
    </div>
  );
};

// ============================================================
// KYC DOCUMENT UPLOADER — uploads to kyc-documents bucket
// ============================================================
const KycImageUploader = ({ label, hint, providerId, fieldKey, currentUrl, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview]     = useState(currentUrl || null);
  const [error, setError]         = useState("");
  const inputRef                  = useRef(null);

  const handleFile = async (file) => {
    setError("");
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please select an image (JPG, PNG, WEBP)."); return; }
    if (file.size > 5 * 1024 * 1024)    { setError("Image must be under 5 MB."); return; }
    setUploading(true);

    const ext  = file.name.split(".").pop().toLowerCase() || "jpg";
    const path = `${providerId}/${fieldKey}.${ext}`;

    // Delete any old file at this path first
    await supabase.storage.from("kyc-documents").remove([path]);

    const { error: upErr } = await supabase.storage
      .from("kyc-documents")
      .upload(path, file, { upsert: true, contentType: file.type });

    if (upErr) { setError("Upload failed. Please try again."); setUploading(false); return; }

    const { data } = supabase.storage.from("kyc-documents").getPublicUrl(path);

    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(file);

    setUploading(false);
    onUploaded(data.publicUrl, fieldKey);
    svcToast("Photo uploaded!", "success");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={labelStyle}>{label}</label>
      {hint && <div style={{ fontSize: 11, color: "rgba(250,248,244,0.35)", marginBottom: 2 }}>{hint}</div>}
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        style={{
          border: `2px dashed ${error ? "rgba(217,48,37,0.5)" : "rgba(201,168,76,0.25)"}`,
          borderRadius: 12, background: "rgba(255,255,255,0.02)",
          cursor: uploading ? "not-allowed" : "pointer",
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", minHeight: 100, position: "relative", overflow: "hidden",
        }}
      >
        {preview ? (
          <img src={preview} alt={label} style={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 10 }} />
        ) : uploading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: 16 }}>
            <div style={{ width: 28, height: 28, border: "3px solid rgba(201,168,76,0.2)", borderTopColor: "#C9A84C", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <span style={{ fontSize: 12, color: "rgba(250,248,244,0.45)" }}>Uploading…</span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: 16 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <span style={{ fontSize: 12, color: "rgba(250,248,244,0.4)", textAlign: "center" }}>Tap to upload<br /><span style={{ fontSize: 10, color: "rgba(250,248,244,0.25)" }}>JPG, PNG or WEBP · max 5 MB</span></span>
          </div>
        )}
      </div>
      {error && <div style={{ fontSize: 12, color: "#D93025" }}>{error}</div>}
      {preview && !uploading && (
        <button onClick={() => { setPreview(null); onUploaded(null, fieldKey); }}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "rgba(250,248,244,0.35)", textDecoration: "underline", textAlign: "left", padding: 0 }}>
          Remove photo
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }}
        onChange={e => handleFile(e.target.files?.[0])} />
    </div>
  );
};

// ============================================================
// PROVIDER AUTH  — Sign Up / Log In for Tradespeople
// ============================================================
const TRADES_SECRET_QUESTIONS = [
  "What was the name of your first employer?",
  "What was the name of the street you grew up on?",
  "What was your mother's maiden name?",
  "What was the name of your first pet?",
  "What was the name of your primary school?",
  "What was the last place you worked at?",
];

const ProviderAuth = ({ onAuth, onBack }) => {
  const [mode, setMode]       = useState("login"); // "login" | "signup" | "forgot"
  const [step, setStep]       = useState(1);       // signup: 1=details, 2=account
  const [loading, setLoading] = useState(false);

  // Login fields
  const [loginEmail, setLoginEmail]   = useState("");
  const [loginPhone, setLoginPhone]   = useState("");
  const [loginPass, setLoginPass]     = useState("");
  const [showLoginPass, setShowLoginPass] = useState(false);
  const [showPass, setShowPass]           = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  // Forgot password flow
  const [forgotIdentifier, setForgotIdentifier] = useState("");
  const [forgotStep, setForgotStep]             = useState(1); // 1=lookup, 2=answer question, 3=new password
  const [forgotRecord, setForgotRecord]         = useState(null);
  const [forgotAnswer, setForgotAnswer]         = useState("");
  const [forgotNewPass, setForgotNewPass]       = useState("");
  const [forgotConfirm, setForgotConfirm]       = useState("");
  const [showForgotPass, setShowForgotPass]     = useState(false);

  // Signup fields
  const [form, setForm] = useState({
    name: "", trades: [], region: "", village: "", phone: "", whatsapp: "",
    bio: "", years_exp: "", rate_per_day: "", photo_url: "",
    email: "", password: "", confirm_password: "",
    secret_question: TRADES_SECRET_QUESTIONS[0], secret_answer: "",
    lat: null, lng: null,
  });
  const [savedProvider, setSavedProvider] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── FORGOT PASSWORD ────────────────────────────────────────
  const handleForgotLookup = async () => {
    if (!forgotIdentifier.trim()) {
      svcToast("Please enter your email or phone number.", "error"); return;
    }
    setLoading(true);
    const isEmail = forgotIdentifier.includes("@");
    let query = supabase.from("service_providers").select("id, name, secret_question, secret_answer");
    if (isEmail) query = query.ilike("email", forgotIdentifier.trim());
    else         query = query.eq("phone", forgotIdentifier.trim());
    const { data, error } = await query.maybeSingle();
    setLoading(false);
    if (error || !data) {
      svcToast("No account found with that email or phone.", "error"); return;
    }
    if (!data.secret_question || !data.secret_answer) {
      svcToast("This account has no security question set. Please contact support on WhatsApp +592 672 6828.", "error"); return;
    }
    setForgotRecord(data);
    setForgotStep(2);
  };

  const handleForgotVerify = () => {
    if (!forgotAnswer.trim()) {
      svcToast("Please answer the security question.", "error"); return;
    }
    if (forgotAnswer.trim().toLowerCase() !== forgotRecord.secret_answer.toLowerCase()) {
      svcToast("Incorrect answer. Please try again.", "error"); return;
    }
    setForgotStep(3);
  };

  const handleForgotReset = async () => {
    if (!forgotNewPass || forgotNewPass.length < 6) {
      svcToast("Password must be at least 6 characters.", "error"); return;
    }
    if (forgotNewPass !== forgotConfirm) {
      svcToast("Passwords do not match.", "error"); return;
    }
    setLoading(true);
    const { error } = await supabase
      .from("service_providers")
      .update({ password_plain: forgotNewPass })
      .eq("id", forgotRecord.id);
    setLoading(false);
    if (error) { svcToast("Could not reset password. Please try again.", "error"); return; }
    svcToast("Password reset! Please sign in with your new password.", "success");
    setMode("login");
    setForgotStep(1);
    setForgotIdentifier(""); setForgotRecord(null);
    setForgotAnswer(""); setForgotNewPass(""); setForgotConfirm("");
  };

  // ── LOGIN ─────────────────────────────────────────────────
  const handleLogin = async () => {
    if ((!loginEmail && !loginPhone) || !loginPass) {
      svcToast("Please enter your email or phone and password.", "error"); return;
    }
    setLoading(true);
    // Look up by email or phone
    let query = supabase.from("service_providers").select("*").in("status", ["active", "kyc_pending", "verified"]).is("deleted_at", null);
    if (loginEmail) query = query.ilike("email", loginEmail.trim());
    else            query = query.eq("phone", loginPhone.trim());
    const { data, error } = await query.single();
    setLoading(false);
    if (error || !data) {
      svcToast("No account found. Check your details or contact support.", "error"); return;
    }
    // Simple password check — in production use Supabase Auth proper
    if (data.password_plain !== loginPass) {
      svcToast("Incorrect password. Please try again.", "error"); return;
    }
    svcToast("Welcome back, " + data.name + "!", "success");
    onAuth(data);
  };

  // ── SIGNUP STEP 1: Details ─────────────────────────────────
  const handleStep1 = () => {
    if (!form.name || !form.trades.length || !form.region || !form.phone) {
      svcToast("Please fill in all required fields.", "error"); return;
    }
    setStep(2);
  };

  // ── SIGNUP STEP 2: Account credentials ───────────────────
  const handleStep2 = async () => {
    if (!form.email || !form.password) {
      svcToast("Email and password are required.", "error"); return;
    }
    if (form.password.length < 6) {
      svcToast("Password must be at least 6 characters.", "error"); return;
    }
    if (form.password !== form.confirm_password) {
      svcToast("Passwords do not match.", "error"); return;
    }
    if (!form.secret_answer.trim()) {
      svcToast("Please answer your security question.", "error"); return;
    }
    // Check email not already used
    const { data: existing } = await supabase
      .from("service_providers").select("id").ilike("email", form.email).maybeSingle();
    if (existing) {
      svcToast("An account with that email already exists. Please log in.", "error"); return;
    }
    setLoading(true);
    const { data, error } = await supabase.from("service_providers").insert([{
      name:            form.name,
      trades:          form.trades,
      trade:           form.trades[0],
      region:          form.region,
      village:         form.village && form.village !== "__other__" ? form.village : null,
      phone:           form.phone,
      whatsapp:        form.whatsapp || form.phone,
      bio:             form.bio,
      years_exp:       parseInt(form.years_exp) || 0,
      rate_per_day:    parseInt(form.rate_per_day) || null,
      photo_url:       form.photo_url || null,
      email:           form.email.trim().toLowerCase(),
      password_plain:  form.password,
      secret_question: form.secret_question,
      secret_answer:   form.secret_answer.trim().toLowerCase(),
      status:          "active",  // free tier — goes live immediately
      avg_rating:      0,
      review_count:    0,
      lat:             form.lat   || null,
      lng:             form.lng   || null,
      created_at:      new Date().toISOString(),
    }]).select().single();
    setLoading(false);
    if (error) { svcToast("Could not create account. Please try again.", "error"); return; }
    setSavedProvider(data);
    svcToast("You're live! Welcome to HireSpot GY 🎉", "success");
    onAuth(data);
  };

  const primaryTrade = TRADES.find(t => t.id === form.trades?.[0]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", backdropFilter: "blur(12px)", zIndex: 1001, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onMouseDown={e => e.target === e.currentTarget && onBack()}>
      {/* Sheet sits above the bottom nav (64px) + device safe-area */}
      <div
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        style={{
        background: "linear-gradient(180deg, #1E1B18 0%, #161310 100%)",
        borderRadius: "24px 24px 0 0",
        width: "100%",
        maxWidth: 560,
        /* key fix: subtract bottom-nav height AND safe-area so nothing is clipped */
        maxHeight: "calc(100svh - 68px - env(safe-area-inset-bottom, 16px))",
        display: "flex",
        flexDirection: "column",
        animation: "slideUp 0.38s cubic-bezier(0.32,0.72,0,1)",
        boxShadow: "0 -1px 0 rgba(201,168,76,0.18), 0 -32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.05)",
      }}>
        {/* Drag handle */}
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 14, paddingBottom: 2, flexShrink: 0 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "rgba(250,248,244,0.14)" }} />
        </div>

        {/* Fixed header */}
        <div style={{ padding: "10px 24px 0", flexShrink: 0 }}>
          {/* Gold accent line */}
          <div style={{ height: 1, background: "linear-gradient(90deg,transparent,rgba(201,168,76,0.3),transparent)", marginBottom: 18 }} />
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6, gap: 12 }}>
            <div>
              <div style={{ fontFamily: "Syne,sans-serif", fontSize: 22, fontWeight: 800, color: "#FAF8F4", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
                {mode === "login" ? "Welcome Back" : mode === "forgot" ? "Reset Password" : "List Your Trade"}
              </div>
              <div style={{ fontSize: 13, color: "rgba(250,248,244,0.38)", marginTop: 5, lineHeight: 1.5 }}>
                {mode === "login"
                  ? "Access your listing and dashboard"
                  : mode === "forgot"
                  ? forgotStep === 1 ? "Enter your email or phone to continue"
                    : forgotStep === 2 ? "Answer your security question"
                    : "Choose a new password"
                  : step === 1 ? "Tell clients about your skills — it's free"
                  : "Secure your account to manage your listing"}
              </div>
            </div>
            <button onClick={onBack} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 12, width: 38, height: 38, minWidth: 38, cursor: "pointer", fontSize: 18, color: "rgba(250,248,244,0.45)", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s", marginTop: 2 }}>×</button>
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{ overflowY: "auto", flex: 1, padding: "16px 24px 32px", WebkitOverflowScrolling: "touch" }}>
          <div style={{ paddingBottom: 4 }}>

        {/* Mode toggle */}
        {mode === "forgot" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Step 1 — lookup */}
            {forgotStep === 1 && (<>
              <Field label="Email or Phone">
                <input placeholder="your@email.com or 6XX-XXXX" style={inputStyle}
                  value={forgotIdentifier} onChange={e => setForgotIdentifier(e.target.value)} />
              </Field>
              <button onClick={handleForgotLookup} disabled={loading}
                style={{ background: "#C9A84C", color: "#0E0D0B", border: "none", borderRadius: 12, padding: "13px 0", fontSize: 15, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, fontFamily: "Syne,sans-serif", marginTop: 4 }}>
                {loading ? "Looking up…" : "Continue →"}
              </button>
              <button onClick={() => setMode("login")}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "rgba(250,248,244,0.38)", fontFamily: "DM Sans,sans-serif", textDecoration: "underline", textUnderlineOffset: 3, padding: 0, textAlign: "center" }}>
                ← Back to Sign In
              </button>
            </>)}

            {/* Step 2 — secret question */}
            {forgotStep === 2 && forgotRecord && (<>
              <div style={{ background: "rgba(184,146,42,0.07)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 12, padding: "14px 16px", fontSize: 13, color: "rgba(250,248,244,0.65)", lineHeight: 1.6 }}>
                <div style={{ fontWeight: 700, color: "#C9A84C", marginBottom: 4 }}>Security Question</div>
                {forgotRecord.secret_question}
              </div>
              <Field label="Your Answer">
                <input placeholder="Type your answer…" style={inputStyle}
                  value={forgotAnswer} onChange={e => setForgotAnswer(e.target.value)} />
              </Field>
              <button onClick={handleForgotVerify}
                style={{ background: "#C9A84C", color: "#0E0D0B", border: "none", borderRadius: 12, padding: "13px 0", fontSize: 15, fontWeight: 800, cursor: "pointer", fontFamily: "Syne,sans-serif", marginTop: 4 }}>
                Verify Answer →
              </button>

              {/* Fallback contact options */}
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 14, marginTop: 4 }}>
                <div style={{ fontSize: 12, color: "rgba(250,248,244,0.3)", textAlign: "center", marginBottom: 10, fontFamily: "DM Sans,sans-serif" }}>
                  Can't remember your answer? We can verify you manually.
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <a href="https://wa.me/5926726828?text=Hi%2C%20I%20need%20help%20resetting%20my%20HireSpot%20password"
                    target="_blank" rel="noopener noreferrer"
                    style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "rgba(37,211,102,0.08)", border: "1px solid rgba(37,211,102,0.25)", borderRadius: 10, padding: "10px 0", fontSize: 13, fontWeight: 600, color: "#25D366", textDecoration: "none", fontFamily: "DM Sans,sans-serif" }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </a>
                  <a href="mailto:hirespotgy@gmail.com?subject=Password%20Reset%20Request"
                    style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.25)", borderRadius: 10, padding: "10px 0", fontSize: 13, fontWeight: 600, color: "#60A5FA", textDecoration: "none", fontFamily: "DM Sans,sans-serif" }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    Email Us
                  </a>
                </div>
              </div>

              <button onClick={() => setForgotStep(1)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "rgba(250,248,244,0.38)", fontFamily: "DM Sans,sans-serif", textDecoration: "underline", textUnderlineOffset: 3, padding: 0, textAlign: "center" }}>
                ← Back
              </button>
            </>)}

            {/* Step 3 — new password */}
            {forgotStep === 3 && (<>
              <div style={{ background: "rgba(45,158,78,0.08)", border: "1px solid rgba(45,158,78,0.25)", borderRadius: 12, padding: "12px 16px", fontSize: 13, color: "rgba(250,248,244,0.65)" }}>
                ✓ Identity verified. Choose a new password below.
              </div>
              <Field label="New Password">
                <div style={{ position: "relative" }}>
                  <input type={showForgotPass ? "text" : "password"} placeholder="At least 6 characters" style={{ ...inputStyle, paddingRight: 40 }}
                    value={forgotNewPass} onChange={e => setForgotNewPass(e.target.value)} />
                  <button type="button" onClick={() => setShowForgotPass(v => !v)}
                    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.28)", display: "flex", alignItems: "center", padding: 0 }}>
                    {showForgotPass
                      ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    }
                  </button>
                </div>
              </Field>
              <Field label="Confirm New Password">
                <input type="password" placeholder="Repeat new password" style={inputStyle}
                  value={forgotConfirm} onChange={e => setForgotConfirm(e.target.value)} />
              </Field>
              <button onClick={handleForgotReset} disabled={loading}
                style={{ background: "#C9A84C", color: "#0E0D0B", border: "none", borderRadius: 12, padding: "13px 0", fontSize: 15, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, fontFamily: "Syne,sans-serif", marginTop: 4 }}>
                {loading ? "Saving…" : "Save New Password →"}
              </button>
            </>)}

          </div>
        ) : mode === "login" ? (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Field label="Email or Phone">
                <input type="email" inputMode="email" placeholder="your@email.com or 6XX-XXXX" style={inputStyle}
                  value={loginEmail} onChange={e => { setLoginEmail(e.target.value); setLoginPhone(""); }} />
              </Field>
              <Field label="Password">
                <div style={{ position: "relative" }}>
                  <input type={showLoginPass ? "text" : "password"} placeholder="Your password" style={{ ...inputStyle, paddingRight: 40 }}
                    value={loginPass} onChange={e => setLoginPass(e.target.value)} />
                  <button type="button" onClick={() => setShowLoginPass(v => !v)}
                    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.28)", display: "flex", alignItems: "center", padding: 0, minWidth: 24, minHeight: 24 }}>
                    {showLoginPass
                      ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    }
                  </button>
                </div>
              </Field>
              <button onClick={handleLogin} disabled={loading}
                style={{ background: loading ? "rgba(201,168,76,0.4)" : "linear-gradient(135deg,#C9A84C 0%,#E4C76B 50%,#C9A84C 100%)", color: "#0E0D0B", border: "none", borderRadius: 12, padding: "13px 0", fontSize: 15, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, fontFamily: "Syne,sans-serif", marginTop: 6, letterSpacing: "-0.01em", boxShadow: loading ? "none" : "0 4px 24px rgba(201,168,76,0.35), 0 1px 0 rgba(255,255,255,0.2) inset", transition: "all 0.2s" }}>
                {loading ? "Signing in…" : "Sign In →"}
              </button>

              {/* Forgot password */}
              <div style={{ textAlign: "center", marginTop: 4 }}>
                <button onClick={() => { setMode("forgot"); setForgotStep(1); }}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "rgba(250,248,244,0.38)", fontFamily: "DM Sans,sans-serif", textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}>
                  Forgot password?
                </button>
              </div>

              {/* Sign up link */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18 }}>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
                <span style={{ fontSize: 12, color: "rgba(250,248,244,0.3)", whiteSpace: "nowrap" }}>New here?</span>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
              </div>
              <button onClick={() => setMode("signup")}
                style={{ background: "transparent", border: "1px solid rgba(201,168,76,0.35)", borderRadius: 12, padding: "12px 0", fontSize: 14, fontWeight: 700, color: "#C9A84C", cursor: "pointer", fontFamily: "Syne,sans-serif", marginTop: 10, letterSpacing: "-0.01em", transition: "all 0.2s", width: "100%" }}>
                Sign Up or Register Your Trade →
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Step progress bar */}
            <div style={{ display: "flex", gap: 6, marginBottom: 28 }}>
              {["Your Details", "Account"].map((s, i) => (
                <div key={i} style={{ flex: 1 }}>
                  <div style={{ height: 4, borderRadius: 2, background: step > i ? "#C9A84C" : "rgba(255,255,255,0.09)", transition: "background 0.3s", marginBottom: 4 }} />
                  <div style={{ fontSize: 10, color: step > i ? "#C9A84C" : "rgba(250,248,244,0.28)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{s}</div>
                </div>
              ))}
            </div>

            {/* ── STEP 1: Trade details ── */}
            {step === 1 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Field label="Full Name" required>
                  <input placeholder="e.g. Ramesh Persaud" style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} />
                </Field>

                <Field label="Trade / Skill" required>
                  <div style={{ fontSize: 11, color: "rgba(250,248,244,0.35)", marginBottom: 8 }}>
                    Select all that apply — you can list multiple trades
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                    {TRADES.map(t => {
                      const selected = form.trades.includes(t.id);
                      return (
                        <button key={t.id} onClick={() => {
                          const next = selected
                            ? form.trades.filter(id => id !== t.id)
                            : [...form.trades, t.id];
                          set("trades", next);
                        }}
                          style={{ background: selected ? "rgba(201,168,76,0.12)" : "rgba(255,255,255,0.03)", border: `1px solid ${selected ? "rgba(201,168,76,0.45)" : "rgba(255,255,255,0.07)"}`, borderRadius: 10, padding: "10px 6px", cursor: "pointer", fontSize: 11, color: selected ? "#C9A84C" : "rgba(250,248,244,0.45)", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, transition: "all 0.15s", fontFamily: "DM Sans,sans-serif", fontWeight: selected ? 700 : 400, lineHeight: 1.2, textAlign: "center", position: "relative" }}>
                          {selected && (
                            <div style={{ position: "absolute", top: 5, right: 5, width: 14, height: 14, borderRadius: "50%", background: "#C9A84C", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#0E0D0B" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            </div>
                          )}
                          <TradeIcon id={t.id} size={20} color={selected ? "#C9A84C" : "rgba(250,248,244,0.4)"} />
                          <span>{t.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  {form.trades.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                      {form.trades.map(id => {
                        const t = TRADES.find(x => x.id === id);
                        return t ? (
                          <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 20, padding: "4px 10px", fontSize: 11, color: "#C9A84C", fontWeight: 600 }}>
                            {t.label}
                            <span onClick={() => set("trades", form.trades.filter(x => x !== id))} style={{ cursor: "pointer", opacity: 0.6, fontSize: 13, lineHeight: 1 }}>×</span>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </Field>

                <Field label="Area / Region" required>
                  <select value={form.region} onChange={e => { set("region", e.target.value); set("village", ""); }} style={{ ...inputStyle, appearance: "none", color: form.region ? "#FAF8F4" : "rgba(250,248,244,0.28)" }}>
                    <option value="">Select your area…</option>
                    {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>

                {form.region && (
                  <Field label="Village / Community">
                    {(VILLAGES_BY_REGION[form.region]?.length > 0) ? (
                      <select value={form.village || ""} onChange={e => set("village", e.target.value)} style={{ ...inputStyle, appearance: "none", color: form.village ? "#FAF8F4" : "rgba(250,248,244,0.28)" }}>
                        <option value="">Select or type below…</option>
                        {VILLAGES_BY_REGION[form.region].map(v => <option key={v} value={v}>{v}</option>)}
                        <option value="__other__">Other (type below)</option>
                      </select>
                    ) : null}
                    {(!VILLAGES_BY_REGION[form.region]?.length || form.village === "__other__") && (
                      <input placeholder="e.g. Kitty, Eccles, Rose Hall…" style={{ ...inputStyle, marginTop: VILLAGES_BY_REGION[form.region]?.length ? 8 : 0 }} value={form.village === "__other__" ? "" : (form.village || "")} onChange={e => set("village", e.target.value)} />
                    )}
                    <div style={{ fontSize: 11, color: "rgba(250,248,244,0.28)", marginTop: 4 }}>Helps clients find tradespeople closer to them</div>
                  </Field>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <Field label="Phone Number" required>
                    <input type="tel" inputMode="tel" placeholder="e.g. 6XX-XXXX" style={inputStyle} value={form.phone} onChange={e => set("phone", e.target.value)} />
                  </Field>
                  <Field label="WhatsApp">
                    <input type="tel" inputMode="tel" placeholder="Same as phone?" style={inputStyle} value={form.whatsapp} onChange={e => set("whatsapp", e.target.value)} />
                  </Field>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <Field label="Years of Experience">
                    <input type="number" inputMode="numeric" placeholder="e.g. 5" style={inputStyle} value={form.years_exp} onChange={e => set("years_exp", e.target.value)} />
                  </Field>
                  <Field label="Daily Rate (GYD)">
                    <input type="number" inputMode="numeric" placeholder="e.g. 8000" style={inputStyle} value={form.rate_per_day} onChange={e => set("rate_per_day", e.target.value)} />
                  </Field>
                </div>

                <Field label="Short Bio">
                  <textarea rows={3} placeholder="Describe your work, specialties, certifications…"
                    style={{ ...inputStyle, resize: "vertical" }}
                    value={form.bio} onChange={e => set("bio", e.target.value)} />
                </Field>

                <LeafletMapPicker
                  region={form.region}
                  lat={form.lat}
                  lng={form.lng}
                  onChange={(lat, lng) => setForm(p => ({ ...p, lat, lng }))}
                />

                <PhotoUploader
                  currentUrl={form.photo_url}
                  providerId={null}
                  onUploaded={url => set("photo_url", url)}
                />

                <button onClick={handleStep1}
                  style={{ background: "#C9A84C", color: "#0E0D0B", border: "none", borderRadius: 10, padding: "14px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "Syne,sans-serif", marginTop: 4 }}>
                  Next: Create Account →
                </button>
              </div>
            )}

            {/* ── STEP 2: Account credentials ── */}
            {step === 2 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ background: "rgba(184,146,42,0.07)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 12, padding: "14px 16px", fontSize: 13, color: "rgba(250,248,244,0.55)", lineHeight: 1.6 }}>
                  Create a login so you can <strong style={{ color: "#FAF8F4" }}>edit your listing, view your reviews, and manage your profile</strong> any time.
                </div>

                <Field label="Email Address" required>
                  <input type="email" placeholder="your@email.com" style={inputStyle} value={form.email} onChange={e => set("email", e.target.value)} />
                </Field>

                <Field label="Password" required>
                  <div style={{ position: "relative" }}>
                    <input type={showPass ? "text" : "password"} placeholder="At least 6 characters" style={{ ...inputStyle, paddingRight: 40 }} value={form.password} onChange={e => set("password", e.target.value)} />
                    <button type="button" onClick={() => setShowPass(v => !v)}
                      style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.28)", display: "flex", alignItems: "center", padding: 0, minWidth: 24, minHeight: 24 }}>
                      {showPass
                        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      }
                    </button>
                  </div>
                </Field>

                <Field label="Confirm Password" required>
                  <div style={{ position: "relative" }}>
                    <input type={showConfirmPass ? "text" : "password"} placeholder="Repeat your password" style={{ ...inputStyle, paddingRight: 40 }} value={form.confirm_password} onChange={e => set("confirm_password", e.target.value)} />
                    <button type="button" onClick={() => setShowConfirmPass(v => !v)}
                      style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.28)", display: "flex", alignItems: "center", padding: 0, minWidth: 24, minHeight: 24 }}>
                      {showConfirmPass
                        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      }
                    </button>
                  </div>
                </Field>

                {/* Account recovery */}
                <div style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "4px 0" }} />
                <div style={{ background: "rgba(184,146,42,0.07)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 12, padding: "12px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#C9A84C", marginBottom: 3 }}>Account Recovery</div>
                  <div style={{ fontSize: 12, color: "rgba(250,248,244,0.45)", lineHeight: 1.5 }}>If you ever forget your password, we'll verify your identity with this question.</div>
                </div>

                <Field label="Security Question" required>
                  <select style={{ ...inputStyle, appearance: "none" }} value={form.secret_question} onChange={e => set("secret_question", e.target.value)}>
                    {TRADES_SECRET_QUESTIONS.map(q => <option key={q} value={q} style={{ background: "#1A1714" }}>{q}</option>)}
                  </select>
                </Field>

                <Field label="Your Answer" required>
                  <input placeholder="Only you should know this" style={inputStyle}
                    value={form.secret_answer} onChange={e => set("secret_answer", e.target.value)} />
                </Field>

                <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                  <button onClick={() => setStep(1)}
                    style={{ flex: 1, background: "transparent", color: "rgba(250,248,244,0.55)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "13px 0", fontSize: 14, cursor: "pointer", fontFamily: "DM Sans,sans-serif" }}>
                    ← Back
                  </button>
                  <button onClick={handleStep2} disabled={loading}
                    style={{ flex: 2, background: "#C9A84C", color: "#0E0D0B", border: "none", borderRadius: 10, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1, fontFamily: "Syne,sans-serif" }}>
                    {loading ? "Creating account…" : "Go Live — It's Free →"}
                  </button>
                </div>
              </div>
            )}

          </>
        )}
          </div>
        </div>
        {/* ── STICKY TOGGLE FOOTER — always visible above bottom nav ── */}
        {mode !== "forgot" && (
        <div style={{
          flexShrink: 0,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          background: "linear-gradient(180deg,rgba(22,19,16,0.97) 0%,rgba(18,15,12,0.99) 100%)",
          backdropFilter: "blur(16px)",
          padding: "16px 24px 20px",
          textAlign: "center",
        }}>
          {mode === "login" ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <span style={{ fontSize: 13, color: "rgba(250,248,244,0.35)", fontFamily: "DM Sans,sans-serif" }}>Not listed yet?</span>
              <button
                onClick={() => { setMode("signup"); setStep(1); }}
                style={{
                  background: "rgba(201,168,76,0.1)",
                  border: "1px solid rgba(201,168,76,0.35)",
                  color: "#C9A84C",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "DM Sans,sans-serif",
                  fontSize: 13,
                  padding: "7px 14px",
                  borderRadius: 8,
                  letterSpacing: "0.01em",
                  transition: "all 0.15s",
                }}>
                Register your trade →
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <span style={{ fontSize: 13, color: "rgba(250,248,244,0.35)", fontFamily: "DM Sans,sans-serif" }}>Already registered?</span>
              <button
                onClick={() => { setMode("login"); setStep(1); }}
                style={{
                  background: "rgba(201,168,76,0.1)",
                  border: "1px solid rgba(201,168,76,0.35)",
                  color: "#C9A84C",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "DM Sans,sans-serif",
                  fontSize: 13,
                  padding: "7px 14px",
                  borderRadius: 8,
                  letterSpacing: "0.01em",
                  transition: "all 0.15s",
                }}>
                Sign in →
              </button>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// KYC SECTION — shown inside ProviderDashboard overview tab
// ============================================================
const KycSection = ({ provider, onStatusChange }) => {
  const [idUrl, setIdUrl]         = useState(provider.kyc_id_url || null);
  const [selfieUrl, setSelfieUrl] = useState(provider.kyc_selfie_url || null);
  const [submitting, setSubmitting] = useState(false);

  const handleUploaded = (url, field) => {
    if (field === "kyc_id")     setIdUrl(url);
    if (field === "kyc_selfie") setSelfieUrl(url);
  };

  const handleSubmit = async () => {
    if (!idUrl || !selfieUrl) {
      svcToast("Please upload both photos before submitting.", "error"); return;
    }
    setSubmitting(true);
    const { data, error } = await supabase
      .from("service_providers")
      .update({ status: "kyc_pending", kyc_id_url: idUrl, kyc_selfie_url: selfieUrl, rejection_reason: null })
      .eq("id", provider.id)
      .select().single();
    setSubmitting(false);
    if (error) { svcToast("Could not submit. Please try again.", "error"); return; }
    svcToast("Verification submitted! Admin will review shortly.", "success");
    onStatusChange(data);
  };

  // ── Already verified ─────────────────────────────────────
  if (provider.status === "verified") {
    return (
      <div style={{ background: "rgba(29,155,240,0.06)", border: "1px solid rgba(29,155,240,0.25)", borderRadius: 14, padding: "18px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#1D9BF0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1D9BF0" }}>You're Verified</div>
            <div style={{ fontSize: 12, color: "rgba(250,248,244,0.45)", marginTop: 2 }}>Your profile displays the verified badge visible to all clients.</div>
          </div>
        </div>
      </div>
    );
  }

  // ── Pending review ────────────────────────────────────────
  if (provider.status === "kyc_pending") {
    return (
      <div style={{ background: "rgba(184,146,42,0.06)", border: "1px solid rgba(184,146,42,0.25)", borderRadius: 14, padding: "18px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(201,168,76,0.15)", border: "1px solid rgba(201,168,76,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#C9A84C" }}>Verification Under Review</div>
            <div style={{ fontSize: 12, color: "rgba(250,248,244,0.45)", marginTop: 2 }}>Your documents have been submitted. Admin will review within 1–2 business days.</div>
          </div>
        </div>
      </div>
    );
  }

  // ── Suspended for non-KYC reason — hide section entirely ────
  if (provider.status === "suspended" && !provider.rejection_reason) return null;

  // ── Active (not verified) OR suspended-after-rejection — show upload form ──
  return (
    <div style={{ background: "#1A1714", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 14, padding: "18px 20px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(250,248,244,0.28)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>Get Verified</div>
      <p style={{ fontSize: 13, color: "rgba(250,248,244,0.5)", lineHeight: 1.6, marginBottom: 16 }}>
        Upload two photos to get your verified badge. Verified tradespeople appear higher in search results and earn more trust from clients.
      </p>

      {/* Show rejection reason if previously rejected */}
      {provider.rejection_reason && (
        <div style={{ background: "rgba(217,48,37,0.08)", border: "1px solid rgba(217,48,37,0.25)", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#D93025", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Previous Submission Rejected</div>
          <div style={{ fontSize: 13, color: "rgba(250,248,244,0.6)", lineHeight: 1.5 }}>{provider.rejection_reason}</div>
          <div style={{ fontSize: 11, color: "rgba(250,248,244,0.35)", marginTop: 6 }}>Please resubmit with clearer photos addressing the issue above.</div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <KycImageUploader
          label="Government ID"
          hint="National ID, passport, or driver's licence"
          providerId={provider.id}
          fieldKey="kyc_id"
          currentUrl={provider.kyc_id_url}
          onUploaded={handleUploaded}
        />
        <KycImageUploader
          label="Selfie Holding ID"
          hint="Hold your ID clearly near your face"
          providerId={provider.id}
          fieldKey="kyc_selfie"
          currentUrl={provider.kyc_selfie_url}
          onUploaded={handleUploaded}
        />
      </div>

      <button
        onClick={handleSubmit}
        disabled={submitting || !idUrl || !selfieUrl}
        style={{
          width: "100%", background: idUrl && selfieUrl ? "#C9A84C" : "rgba(201,168,76,0.25)",
          color: idUrl && selfieUrl ? "#0E0D0B" : "rgba(250,248,244,0.4)",
          border: "none", borderRadius: 10, padding: "13px 0", fontSize: 14,
          fontWeight: 700, cursor: idUrl && selfieUrl && !submitting ? "pointer" : "not-allowed",
          fontFamily: "Syne,sans-serif", transition: "all 0.2s",
        }}
      >
        {submitting ? "Submitting…" : "Submit for Verification →"}
      </button>
    </div>
  );
};

// ============================================================
// PROVIDER DASHBOARD  — Tradesperson's own account screen
// ============================================================
const ProviderDashboard = ({ provider: initialProvider, onLogout, onBack }) => {
  const [provider, setProvider]   = useState(initialProvider);
  const [reviews, setReviews]     = useState([]);
  const [activeTab, setActiveTab] = useState("overview"); // overview | edit | reviews
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [edit, setEdit]           = useState({
    bio: initialProvider.bio || "",
    phone: initialProvider.phone || "",
    whatsapp: initialProvider.whatsapp || "",
    rate_per_day: initialProvider.rate_per_day || "",
    years_exp: initialProvider.years_exp || "",
    photo_url: initialProvider.photo_url || "",
    region: initialProvider.region || "",
    village: initialProvider.village || "",
  });

  const providerTrades = (provider.trades?.length ? provider.trades : provider.trade ? [provider.trade] : [])
    .map(id => TRADES.find(t => t.id === id)).filter(Boolean);
  const isActive = provider.status === "active" || provider.status === "verified";
  const isPending = provider.status === "pending";

  const subExpiry = provider.subscription_expires_at
    ? new Date(provider.subscription_expires_at)
    : null;
  const daysLeft = subExpiry
    ? Math.ceil((subExpiry - Date.now()) / 86400000)
    : null;

  useEffect(() => {
    if (activeTab === "reviews") {
      setLoading(true);
      supabase.from("service_reviews").select("*").eq("provider_id", provider.id)
        .order("created_at", { ascending: false })
        .then(({ data }) => { setReviews(data || []); setLoading(false); });
    }
  }, [activeTab, provider.id]);

  const handleSaveProfile = async () => {
    setSaving(true);
    const { data, error } = await supabase.from("service_providers")
      .update({
        bio:          edit.bio,
        phone:        edit.phone,
        whatsapp:     edit.whatsapp || edit.phone,
        rate_per_day: parseInt(edit.rate_per_day) || null,
        years_exp:    parseInt(edit.years_exp) || 0,
        photo_url:    edit.photo_url || null,
        region:       edit.region,
        village:      edit.village || null,
      })
      .eq("id", provider.id)
      .select().single();
    setSaving(false);
    if (error) { svcToast("Could not save changes.", "error"); return; }
    setProvider(data);
    svcToast("Profile updated!", "success");
    setActiveTab("overview");
  };

  const tabStyle = (t) => ({
    padding: "10px 16px", fontSize: 13, fontWeight: 600,
    border: "none", background: "transparent", cursor: "pointer",
    borderBottom: `2px solid ${activeTab === t ? "#C9A84C" : "transparent"}`,
    color: activeTab === t ? "#C9A84C" : "rgba(250,248,244,0.55)",
    fontFamily: "DM Sans,sans-serif", transition: "all 0.15s",
  });

  return (
    <div className="hirespot-root" style={{ minHeight: "100vh", background: "#0F0E0C", paddingBottom: "calc(100px + env(safe-area-inset-bottom))" }}>

      {/* Header */}
      <div style={{ background: "#1C1917", borderBottom: "1px solid rgba(255,255,255,0.07)", position: "sticky", top: 0, zIndex: 80 }}>
        <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 60 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button onClick={onBack}
                style={{ background: "rgba(255,255,255,0.07)", border: "none", borderRadius: 10, width: 44, height: 44, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(250,248,244,0.6)" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <div>
                <div style={{ fontFamily: "Syne,sans-serif", fontSize: 15, fontWeight: 800, color: "#FAF8F4" }}>My Trades Dashboard</div>
                <div style={{ fontSize: 10, color: "rgba(250,248,244,0.35)", marginTop: -1 }}>HireSpot GY</div>
              </div>
            </div>
            <button onClick={onLogout}
              style={{ background: "rgba(217,48,37,0.12)", border: "1px solid rgba(217,48,37,0.25)", borderRadius: 8, padding: "6px 14px", fontSize: 12, color: "#D93025", cursor: "pointer", fontFamily: "DM Sans,sans-serif", fontWeight: 600 }}>
              Log Out
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px 20px" }}>

        {/* Profile hero card */}
        <div style={{ background: "linear-gradient(135deg,#1C1917,#2A2520)", border: "1px solid rgba(184,146,42,0.15)", borderRadius: 20, padding: "28px 24px", marginBottom: 24, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "radial-gradient(circle,rgba(184,146,42,0.08),transparent 70%)", pointerEvents: "none" }} />
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: "rgba(184,146,42,0.15)", border: "2px solid rgba(184,146,42,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
              {provider.photo_url
                ? <img src={provider.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <TradeIcon id={providerTrades[0]?.id || "handyman"} size={26} color="#C9A84C" />}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 2, flexWrap: "wrap" }}>
                <div style={{ fontFamily: "Syne,sans-serif", fontSize: 20, fontWeight: 800, color: "#FAF8F4" }}>{provider.name}</div>
                {provider.status === "verified" && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(29,155,240,0.12)", border: "1px solid rgba(29,155,240,0.35)", borderRadius: 20, padding: "3px 9px 3px 5px" }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#1D9BF0", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 8px rgba(29,155,240,0.5)" }}>
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#1D9BF0", letterSpacing: "0.03em" }}>Verified</span>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 13, color: "rgba(250,248,244,0.45)" }}>{providerTrades.map(t => t.label).join(" · ")} · {provider.region}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                <Stars rating={provider.avg_rating} size={13} />
                <span style={{ fontSize: 12, color: "#B8922A", fontWeight: 600 }}>{provider.avg_rating || "No ratings yet"}</span>
                <span style={{ fontSize: 12, color: "rgba(250,248,244,0.3)" }}>({provider.review_count} reviews)</span>
              </div>
            </div>
          </div>

          {/* Status + subscription strip */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 20 }}>
              <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 11, color: "rgba(250,248,244,0.3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Status</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: isActive ? "#2D9E4E" : "#D93025", flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: isActive ? "#2D9E4E" : "#D93025", textTransform: "capitalize" }}>
                    {provider.status === "verified" ? "Verified" : isActive ? "Live" : provider.status}
                  </span>
                </div>
              </div>
              <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 11, color: "rgba(250,248,244,0.3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Daily Rate</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#FAF8F4" }}>
                  {provider.rate_per_day ? `GYD ${Number(provider.rate_per_day).toLocaleString()}` : "—"}
                </div>
              </div>
            </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.09)", marginBottom: 24 }}>
          <button style={tabStyle("overview")} onClick={() => setActiveTab("overview")}>Overview</button>
          <button style={tabStyle("edit")} onClick={() => setActiveTab("edit")}>Edit Profile</button>
          <button style={tabStyle("reviews")} onClick={() => setActiveTab("reviews")}>Reviews ({provider.review_count})</button>
        </div>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === "overview" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {provider.bio && (
              <div style={{ background: "#1A1714", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 14, padding: "18px 20px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(250,248,244,0.28)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>Your Bio</div>
                <p style={{ fontSize: 14, color: "rgba(250,248,244,0.55)", lineHeight: 1.7 }}>{provider.bio}</p>
              </div>
            )}

            <div style={{ background: "#1A1714", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 14, padding: "18px 20px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(250,248,244,0.28)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14 }}>Contact Info</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { label: "Phone", value: provider.phone },
                  { label: "WhatsApp", value: provider.whatsapp || provider.phone },
                  { label: "Email", value: provider.email },
                  { label: "Location", value: provider.village ? `${provider.village}, ${provider.region}` : provider.region },
                  { label: "Experience", value: provider.years_exp ? `${provider.years_exp} years` : "—" },
                ].map((r, i) => r.value && (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                    <span style={{ color: "rgba(250,248,244,0.28)" }}>{r.label}</span>
                    <span style={{ color: "#FAF8F4", fontWeight: 500 }}>{r.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: "rgba(37,211,102,0.06)", border: "1px solid rgba(37,211,102,0.18)", borderRadius: 14, padding: "16px 20px", fontSize: 13, color: "rgba(250,248,244,0.55)", lineHeight: 1.6 }}>
              <strong style={{ color: "#FAF8F4" }}>Need help?</strong> Contact us on WhatsApp anytime.{" "}
              <button onClick={() => openWA(ADMIN_WHATSAPP, `Hi, I need help with my HireSpot GY tradesperson listing (${provider.name}).`)}
                style={{ background: "none", border: "none", color: "#25D366", fontWeight: 700, cursor: "pointer", fontFamily: "DM Sans,sans-serif", fontSize: 13, padding: 0 }}>
                Message us →
              </button>
            </div>

            {/* ── GET VERIFIED CARD ── */}
            <KycSection provider={provider} onStatusChange={(updated) => setProvider(updated)} />

          </div>
        )}

        {/* ── EDIT PROFILE TAB ── */}
        {activeTab === "edit" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Field label="Area / Region">
              <select value={edit.region} onChange={e => setEdit(p => ({ ...p, region: e.target.value, village: "" }))}
                style={{ ...inputStyle, appearance: "none" }}>
                {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>

            <Field label="Village / Community">
              <input placeholder="e.g. Kitty, Eccles, Rose Hall…" style={inputStyle}
                value={edit.village} onChange={e => setEdit(p => ({ ...p, village: e.target.value }))} />
            </Field>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Phone Number">
                <input type="tel" inputMode="tel" style={inputStyle} value={edit.phone} onChange={e => setEdit(p => ({ ...p, phone: e.target.value }))} />
              </Field>
              <Field label="WhatsApp">
                <input type="tel" inputMode="tel" style={inputStyle} value={edit.whatsapp} onChange={e => setEdit(p => ({ ...p, whatsapp: e.target.value }))} />
              </Field>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Years of Experience">
                <input type="number" inputMode="numeric" style={inputStyle} value={edit.years_exp} onChange={e => setEdit(p => ({ ...p, years_exp: e.target.value }))} />
              </Field>
              <Field label="Daily Rate (GYD)">
                <input type="number" inputMode="numeric" style={inputStyle} value={edit.rate_per_day} onChange={e => setEdit(p => ({ ...p, rate_per_day: e.target.value }))} />
              </Field>
            </div>

            <Field label="Short Bio">
              <textarea rows={4} style={{ ...inputStyle, resize: "vertical" }}
                value={edit.bio} onChange={e => setEdit(p => ({ ...p, bio: e.target.value }))} />
            </Field>

            <PhotoUploader
              currentUrl={edit.photo_url}
              providerId={provider.id}
              onUploaded={url => setEdit(p => ({ ...p, photo_url: url }))}
            />

            <button onClick={handleSaveProfile} disabled={saving}
              style={{ background: "#C9A84C", color: "#0E0D0B", border: "none", borderRadius: 10, padding: "14px 0", fontSize: 15, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1, fontFamily: "Syne,sans-serif", marginTop: 4 }}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        )}

        {/* ── REVIEWS TAB ── */}
        {activeTab === "reviews" && (
          <div>
            {loading ? (
              <div style={{ textAlign: "center", padding: 40, color: "rgba(250,248,244,0.28)" }}>Loading reviews…</div>
            ) : reviews.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 20px" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>⭐</div>
                <div style={{ fontFamily: "Syne,sans-serif", fontSize: 16, fontWeight: 700, color: "#FAF8F4", marginBottom: 6 }}>No reviews yet</div>
                <div style={{ fontSize: 13, color: "rgba(250,248,244,0.55)" }}>When clients leave reviews, they'll appear here.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 8 }}>
                  <div style={{ background: "#1A1714", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 12, padding: "16px 18px", textAlign: "center" }}>
                    <div style={{ fontFamily: "Syne,sans-serif", fontSize: 32, fontWeight: 800, color: "#C9A84C" }}>{provider.avg_rating || "—"}</div>
                    <Stars rating={provider.avg_rating} size={16} />
                    <div style={{ fontSize: 12, color: "rgba(250,248,244,0.28)", marginTop: 4 }}>Average rating</div>
                  </div>
                  <div style={{ background: "#1A1714", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 12, padding: "16px 18px", textAlign: "center" }}>
                    <div style={{ fontFamily: "Syne,sans-serif", fontSize: 32, fontWeight: 800, color: "#FAF8F4" }}>{reviews.length}</div>
                    <div style={{ fontSize: 12, color: "rgba(250,248,244,0.28)", marginTop: 4 }}>Total reviews</div>
                  </div>
                </div>
                {reviews.map(r => (
                  <div key={r.id} style={{ background: "#1A1714", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 12, padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "#FAF8F4" }}>{r.reviewer_name}</span>
                      <Stars rating={r.rating} size={13} />
                    </div>
                    {r.comment && <p style={{ fontSize: 13, color: "rgba(250,248,244,0.55)", lineHeight: 1.6 }}>{r.comment}</p>}
                    <div style={{ fontSize: 11, color: "rgba(250,248,244,0.28)", marginTop: 6 }}>
                      {new Date(r.created_at).toLocaleDateString("en-GY", { month: "short", day: "numeric", year: "numeric" })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// POST JOB REQUEST MODAL
// ============================================================
// ============================================================
// REVIEW MODAL
// ============================================================
const ReviewModal = ({ provider, onClose }) => {
  const [rating, setRating]   = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName]       = useState("");
  const [phone, setPhone]     = useState("");
  const [saving, setSaving]   = useState(false);
  const [done, setDone]       = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);

  // Normalise phone — strip spaces/dashes for consistent comparison
  const normalisePhone = (p) => p.replace(/\D/g, "");

  const submit = async () => {
    if (!rating) { svcToast("Please select a star rating.", "error"); return; }
    if (!name.trim())  { svcToast("Please enter your name.", "error"); return; }
    if (!phone.trim()) { svcToast("Please enter your phone number.", "error"); return; }

    const normPhone = normalisePhone(phone);
    if (normPhone.length < 7) {
      svcToast("Please enter a valid phone number.", "error"); return;
    }

    setSaving(true);

    // ── 1-review-per-phone check ──────────────────────────────
    const { data: existing } = await supabase
      .from("service_reviews")
      .select("id")
      .eq("provider_id", provider.id)
      .eq("reviewer_phone", normPhone)
      .maybeSingle();

    if (existing) {
      setSaving(false);
      setAlreadyReviewed(true);
      return;
    }

    // ── Insert review ─────────────────────────────────────────
    await supabase.from("service_reviews").insert([{
      provider_id:    provider.id,
      reviewer_name:  name.trim(),
      reviewer_phone: normPhone,
      rating,
      comment:        comment.trim(),
      created_at:     new Date().toISOString(),
    }]);

    // ── Recalculate avg ───────────────────────────────────────
    const { data: allReviews } = await supabase
      .from("service_reviews").select("rating").eq("provider_id", provider.id);
    if (allReviews?.length) {
      const avg = allReviews.reduce((a, r) => a + r.rating, 0) / allReviews.length;
      await supabase.from("service_providers").update({
        avg_rating:   Math.round(avg * 10) / 10,
        review_count: allReviews.length,
      }).eq("id", provider.id);
    }

    setSaving(false);
    setDone(true);
    svcToast("Review submitted!", "success");
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", zIndex: 10001, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#1A1714", borderRadius: "24px 24px 0 0", width: "100%", maxWidth: 560, maxHeight: "calc(100svh - 68px - env(safe-area-inset-bottom, 16px))", overflowY: "auto", paddingBottom: "env(safe-area-inset-bottom)", animation: "slideUp 0.3s cubic-bezier(0.32,0.72,0,1)" }}>
        {/* Drag handle */}
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 12, paddingBottom: 4 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "rgba(250,248,244,0.15)" }} />
        </div>
        <div style={{ padding: "16px 24px 32px" }}>

          {/* ── Already reviewed state ── */}
          {alreadyReviewed ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div style={{ width: 56, height: 56, borderRadius: 18, background: "rgba(217,48,37,0.1)", border: "1px solid rgba(217,48,37,0.25)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D93025" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </div>
              <div style={{ fontFamily: "Syne,sans-serif", fontSize: 17, fontWeight: 800, color: "#FAF8F4", marginBottom: 8 }}>Already Reviewed</div>
              <div style={{ fontSize: 13, color: "rgba(250,248,244,0.45)", lineHeight: 1.6, marginBottom: 24 }}>
                You've already left a review for <strong style={{ color: "#FAF8F4" }}>{provider.name}</strong> from this phone number. Only one review per person is allowed.
              </div>
              <button onClick={onClose} style={{ background: "#C9A84C", color: "#0E0D0B", border: "none", borderRadius: 10, padding: "12px 32px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "Syne,sans-serif" }}>Got It</button>
            </div>

          ) : done ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div style={{ width: 56, height: 56, borderRadius: 18, background: "rgba(45,158,78,0.1)", border: "1px solid rgba(45,158,78,0.25)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2D9E4E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div style={{ fontFamily: "Syne,sans-serif", fontSize: 18, fontWeight: 700, color: "#FAF8F4", marginBottom: 8 }}>Thanks for your review!</div>
              <div style={{ fontSize: 14, color: "rgba(250,248,244,0.55)", marginBottom: 24 }}>Your review helps others find great tradespeople.</div>
              <button onClick={onClose} style={{ background: "#C9A84C", color: "#0E0D0B", border: "none", borderRadius: 10, padding: "12px 32px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "Syne,sans-serif" }}>Done</button>
            </div>

          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <div style={{ fontFamily: "Syne,sans-serif", fontSize: 18, fontWeight: 800, color: "#FAF8F4" }}>Leave a Review</div>
                <button onClick={onClose} style={{ background: "#221E18", border: "none", borderRadius: 10, width: 40, height: 40, cursor: "pointer", fontSize: 20, color: "rgba(250,248,244,0.55)" }}>×</button>
              </div>
              <div style={{ fontSize: 14, color: "rgba(250,248,244,0.55)", marginBottom: 20 }}>How was your experience with <strong style={{ color: "#FAF8F4" }}>{provider.name}</strong>?</div>

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <div style={{ ...labelStyle, marginBottom: 8 }}>Rating</div>
                  <Stars rating={rating} size={28} interactive onChange={setRating} />
                </div>

                <Field label="Your Name" required>
                  <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Marcus Singh" style={inputStyle} />
                </Field>

                <Field label="Phone Number" required>
                  <input type="tel" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder="e.g. 6XX-XXXX"
                    style={inputStyle} />
                  <div style={{ fontSize: 11, color: "rgba(250,248,244,0.28)", marginTop: 4 }}>
                    Used to ensure one review per person — not displayed publicly.
                  </div>
                </Field>

                <Field label="Comments (optional)">
                  <textarea value={comment} onChange={e => setComment(e.target.value)}
                    placeholder="Share your experience…" rows={3} style={{ ...inputStyle, resize: "vertical" }} />
                </Field>

                <button onClick={submit} disabled={saving}
                  style={{ background: "#C9A84C", color: "#0E0D0B", border: "none", borderRadius: 10, padding: "13px 0", fontSize: 14, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1, fontFamily: "Syne,sans-serif" }}>
                  {saving ? "Checking…" : "Submit Review"}
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

// ============================================================
// PROVIDER DETAIL MODAL  (public view)
// ============================================================
const ProviderModal = ({ provider, onClose }) => {
  const [reviews, setReviews]   = useState([]);
  const [showReview, setShowReview] = useState(false);
  const providerTrades = (provider.trades?.length ? provider.trades : provider.trade ? [provider.trade] : [])
    .map(id => TRADES.find(t => t.id === id)).filter(Boolean);
  const primaryTrade = providerTrades[0];

  useEffect(() => {
    supabase.from("service_reviews").select("*").eq("provider_id", provider.id)
      .order("created_at", { ascending: false }).limit(10)
      .then(({ data }) => setReviews(data || []));
  }, [provider.id]);

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)", zIndex: 9998, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
        onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={{ background: "linear-gradient(180deg,#1A1714 0%,#131110 100%)", borderRadius: "28px 28px 0 0", width: "100%", maxWidth: 560, maxHeight: "calc(100svh - 68px - env(safe-area-inset-bottom, 16px))", display: "flex", flexDirection: "column", animation: "slideUp 0.32s cubic-bezier(0.32,0.72,0,1)", boxShadow: "0 -24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)" }}>

          {/* Drag handle */}
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 14, flexShrink: 0 }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(250,248,244,0.1)" }} />
          </div>

          {/* Scrollable content */}
          <div style={{ overflowY: "auto", flex: 1, WebkitOverflowScrolling: "touch", paddingBottom: "calc(32px + env(safe-area-inset-bottom, 16px))" }}>

          {/* Hero header */}
          <div style={{ padding: "20px 24px 24px", position: "relative", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, width: 40, height: 40, cursor: "pointer", fontSize: 18, color: "rgba(250,248,244,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <div style={{ width: 68, height: 68, borderRadius: 18, background: "rgba(201,168,76,0.1)", border: "2px solid rgba(201,168,76,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                {provider.photo_url
                  ? <img src={provider.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <TradeIcon id={primaryTrade?.id || "handyman"} size={30} color="#C9A84C" />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                  <div style={{ fontFamily: "Syne,sans-serif", fontSize: 20, fontWeight: 800, color: "#FAF8F4", letterSpacing: "-0.02em" }}>{provider.name}</div>
                  {provider.status === "verified" && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(29,155,240,0.12)", border: "1px solid rgba(29,155,240,0.35)", borderRadius: 20, padding: "3px 9px 3px 5px" }}>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#1D9BF0", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 8px rgba(29,155,240,0.5)" }}>
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#1D9BF0", letterSpacing: "0.03em" }}>Verified</span>
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 13, color: "rgba(250,248,244,0.45)", marginTop: 3 }}>
                  {providerTrades.map(t => t.label).join(" · ")}
                  {(provider.village || provider.region) && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 8, color: "rgba(250,248,244,0.3)" }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {provider.village ? `${provider.village}, ${provider.region}` : provider.region}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                  <Stars rating={provider.avg_rating} size={14} />
                  <span style={{ fontSize: 12, color: "#C9A84C", fontWeight: 700 }}>{provider.avg_rating > 0 ? provider.avg_rating.toFixed(1) : "New"}</span>
                  <span style={{ fontSize: 12, color: "rgba(250,248,244,0.25)" }}>({provider.review_count} reviews)</span>
                </div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 12 }}>
              {[
                { label: "Experience", value: provider.years_exp ? `${provider.years_exp} yrs` : "—" },
                { label: "Daily Rate",  value: provider.rate_per_day ? `GYD ${Number(provider.rate_per_day).toLocaleString()}` : "Ask" },
                { label: "Reviews",    value: provider.review_count || 0 },
              ].map((s, i) => (
                <div key={i} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "8px 10px" }}>
                  <div style={{ fontFamily: "Syne,sans-serif", fontSize: 14, fontWeight: 800, color: "#FAF8F4", letterSpacing: "-0.02em" }}>{s.value}</div>
                  <div style={{ fontSize: 9, color: "rgba(250,248,244,0.28)", textTransform: "uppercase", letterSpacing: "0.07em", marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* ── Contact buttons — placed here so they're always visible on mobile ── */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
              <button
                onClick={() => openWA(provider.whatsapp || provider.phone, `Hi ${provider.name}, I found your profile on HireSpot GY. I'd like to discuss a job.`)}
                style={{ background: "#25D366", color: "#fff", border: "none", borderRadius: 12, padding: "10px 0", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontFamily: "Syne,sans-serif" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                WhatsApp
              </button>
              <a href={`tel:${provider.phone}`}
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "10px 0", fontSize: 13, fontWeight: 600, color: "#FAF8F4", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, textDecoration: "none", fontFamily: "DM Sans,sans-serif" }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.06 6.06l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                Call
              </a>
            </div>
          </div>

          <div style={{ padding: "14px 24px 24px" }}>
            {provider.bio && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(250,248,244,0.28)", textTransform: "uppercase", letterSpacing: "0.09em", marginBottom: 8 }}>About</div>
                <p style={{ fontSize: 14, color: "rgba(250,248,244,0.6)", lineHeight: 1.75 }}>{provider.bio}</p>
              </div>
            )}

            <ProviderMapChip lat={provider.lat} lng={provider.lng} name={provider.name} />

            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(250,248,244,0.28)", textTransform: "uppercase", letterSpacing: "0.09em" }}>Reviews ({reviews.length})</div>
                <button onClick={() => setShowReview(true)}
                  style={{ background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.22)", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, color: "#C9A84C", cursor: "pointer", fontFamily: "DM Sans,sans-serif", transition: "all 0.15s" }}>
                  + Write Review
                </button>
              </div>
              {reviews.length === 0 ? (
                <div style={{ fontSize: 13, color: "rgba(250,248,244,0.3)", textAlign: "center", padding: "24px 0", border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 12 }}>No reviews yet — be the first!</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {reviews.map(r => (
                    <div key={r.id} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "14px 16px" }}>
                      {/* Name + stars row */}
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: r.comment ? 8 : 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#FAF8F4", lineHeight: 1.4, flex: 1, minWidth: 0, wordBreak: "break-word" }}>{r.reviewer_name}</span>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0 }}>
                          <Stars rating={r.rating} size={13} />
                          <span style={{ fontSize: 10, color: "#C9A84C", fontWeight: 700 }}>{r.rating}.0</span>
                        </div>
                      </div>
                      {/* Comment */}
                      {r.comment && (
                        <p style={{ fontSize: 13, color: "rgba(250,248,244,0.55)", lineHeight: 1.65, margin: "0 0 8px" }}>{r.comment}</p>
                      )}
                      {/* Date */}
                      <div style={{ fontSize: 11, color: "rgba(250,248,244,0.2)" }}>
                        {new Date(r.created_at).toLocaleDateString("en-GY", { month: "short", day: "numeric", year: "numeric" })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          </div>{/* end scrollable */}

        </div>
      </div>
      {showReview && <ReviewModal provider={provider} onClose={() => setShowReview(false)} />}
    </>
  );
};

// ============================================================
// PROVIDER CARD  (browse grid)
// ============================================================
const ProviderCard = ({ provider, onClick }) => {
  const providerTrades = (provider.trades?.length ? provider.trades : provider.trade ? [provider.trade] : [])
    .map(id => TRADES.find(t => t.id === id)).filter(Boolean);
  const primaryTrade = providerTrades[0];
  const [hovered, setHovered] = useState(false);
  return (
    <div onClick={onClick}
      style={{ background: hovered ? "rgba(255,255,255,0.035)" : "rgba(255,255,255,0.02)", border: `1px solid ${hovered ? "rgba(201,168,76,0.3)" : "rgba(255,255,255,0.07)"}`, borderRadius: 18, overflow: "hidden", cursor: "pointer", transition: "all 0.22s ease", display: "flex", flexDirection: "column", transform: hovered ? "translateY(-3px)" : "none", boxShadow: hovered ? "0 16px 48px rgba(0,0,0,0.25), 0 0 0 0 rgba(201,168,76,0.1)" : "0 2px 8px rgba(0,0,0,0.15)" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      {/* Top accent line */}
      <div style={{ height: 2, background: hovered ? "linear-gradient(90deg,#C9A84C,#E8C96A,#C9A84C)" : "transparent", transition: "all 0.22s" }} />
      <div style={{ padding: "18px 18px 0" }}>
        <div style={{ display: "flex", gap: 13, alignItems: "flex-start", marginBottom: 12 }}>
          {/* Avatar */}
          <div style={{ width: 54, height: 54, borderRadius: 15, background: "rgba(201,168,76,0.08)", border: `1.5px solid ${hovered ? "rgba(201,168,76,0.35)" : "rgba(201,168,76,0.15)"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden", transition: "all 0.22s" }}>
            {provider.photo_url
              ? <img src={provider.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <TradeIcon id={primaryTrade?.id || "handyman"} size={22} color={hovered ? "#C9A84C" : "rgba(201,168,76,0.55)"} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2, minWidth: 0 }}>
              <div style={{ fontFamily: "Syne,sans-serif", fontSize: 15, fontWeight: 700, color: "#FAF8F4", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", letterSpacing: "-0.01em" }}>{provider.name}</div>
              {provider.status === "verified" && (
                <div title="Verified tradesperson" style={{ flexShrink: 0, width: 17, height: 17, borderRadius: "50%", background: "#1D9BF0", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 6px rgba(29,155,240,0.5)" }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
              )}
            </div>
            <div style={{ fontSize: 12, color: "rgba(250,248,244,0.45)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {providerTrades.map(t => t.label).join(" · ")}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 5 }}>
              <Stars rating={provider.avg_rating} size={11} />
              <span style={{ fontSize: 11, color: "#C9A84C", fontWeight: 700 }}>{provider.avg_rating > 0 ? provider.avg_rating.toFixed(1) : "New"}</span>
              {provider.review_count > 0 && <span style={{ fontSize: 11, color: "rgba(250,248,244,0.25)" }}>({provider.review_count})</span>}
            </div>
          </div>
        </div>
        {provider.bio && (
          <p style={{ fontSize: 13, color: "rgba(250,248,244,0.4)", lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", marginBottom: 14 }}>
            {provider.bio}
          </p>
        )}
      </div>
      <div style={{ marginTop: "auto", padding: "11px 18px", background: "rgba(0,0,0,0.2)", borderTop: "1px solid rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, color: "rgba(250,248,244,0.3)", display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
          {provider.village ? `${provider.village}, ${provider.region}` : provider.region}
          {provider.years_exp > 0 && <><span style={{ margin: "0 4px", opacity: 0.3 }}>·</span>{provider.years_exp}y exp</>}
        </span>
        {provider.rate_per_day ? (
          <span style={{ fontSize: 11, fontWeight: 700, color: "#C9A84C", fontFamily: "Syne,sans-serif", background: "rgba(201,168,76,0.1)", borderRadius: 6, padding: "2px 7px" }}>
            GYD {Number(provider.rate_per_day).toLocaleString()}/day
          </span>
        ) : null}
      </div>
    </div>
  );
};

// ============================================================
// LANDING MAP  — live gold dots for all providers
// ============================================================
const LandingMap = ({ providers, filterTrade, onSelect }) => {
  const mapId      = useRef("lmap_landing_" + Math.random().toString(36).slice(2));
  const leafletRef = useRef(null);
  const clusterRef = useRef(null);

  const visibleProviders = filterTrade
    ? providers.filter(p => (p.trades?.length ? p.trades : p.trade ? [p.trade] : []).includes(filterTrade))
    : providers;

  useEffect(() => {
    const loadCSS = (id, href) => {
      if (!document.getElementById(id)) {
        const l = document.createElement("link");
        l.id = id; l.rel = "stylesheet"; l.href = href;
        document.head.appendChild(l);
      }
    };
    loadCSS("leaflet-css",  "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css");
    loadCSS("cluster-css",  "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css");
    loadCSS("cluster-css2", "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css");

    const init = () => {
      if (leafletRef.current) return;
      const L = window.L;
      if (!L || !L.markerClusterGroup) return;

      const map = L.map(mapId.current, {
        center: [6.80, -58.16], zoom: 10,
        zoomControl: true, attributionControl: false,
      });

      L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
        maxZoom: 19, subdomains: "abcd",
      }).addTo(map);

      const cluster = L.markerClusterGroup({
        maxClusterRadius: 40,
        iconCreateFunction: (c) => {
          const n = c.getChildCount();
          return L.divIcon({
            className: "",
            html: `<div style="position:relative;width:44px;height:44px;">
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="22" cy="22" r="20" fill="rgba(14,13,11,0.88)" stroke="#C9A84C" stroke-width="1.5"/>
                <circle cx="22" cy="22" r="16" fill="rgba(201,168,76,0.1)" stroke="rgba(201,168,76,0.3)" stroke-width="1"/>
              </svg>
              <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-family:Syne,sans-serif;font-size:13px;font-weight:800;color:#C9A84C;">${n}</div>
            </div>`,
            iconSize: [44, 44], iconAnchor: [22, 22],
          });
        },
      });

      map.addLayer(cluster);
      leafletRef.current = map;
      clusterRef.current = cluster;
    };

    const loadScript = (src, onload) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        if (window.L?.markerClusterGroup && onload) onload();
        else if (window.L && onload) onload();
        else document.querySelector(`script[src="${src}"]`).addEventListener("load", onload);
        return;
      }
      const s = document.createElement("script");
      s.src = src; s.onload = onload;
      document.head.appendChild(s);
    };

    if (window.L?.markerClusterGroup) { init(); }
    else if (window.L) {
      loadScript("https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js", init);
    } else {
      loadScript("https://unpkg.com/leaflet@1.9.4/dist/leaflet.js", () => {
        loadScript("https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js", init);
      });
    }

    return () => {
      if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; clusterRef.current = null; }
    };
  }, []);

  useEffect(() => {
    const cluster = clusterRef.current;
    const L = window.L;
    if (!cluster || !L) return;
    cluster.clearLayers();

    visibleProviders.forEach(p => {
      if (!p.lat || !p.lng) return;
      const providerTrades = (p.trades?.length ? p.trades : p.trade ? [p.trade] : [])
        .map(id => TRADES.find(t => t.id === id)).filter(Boolean);
      const tradeName = providerTrades[0]?.label || "Tradesperson";

      const icon = L.divIcon({
        className: "",
        html: `<div style="position:relative;width:32px;height:40px;filter:drop-shadow(0 4px 8px rgba(0,0,0,0.55)) drop-shadow(0 0 6px rgba(201,168,76,0.45));">
          <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <radialGradient id="pinGrad" cx="40%" cy="30%" r="65%">
                <stop offset="0%" stop-color="#E8C96A"/>
                <stop offset="100%" stop-color="#B8882A"/>
              </radialGradient>
            </defs>
            <path d="M16 0C7.163 0 0 7.163 0 16c0 10 16 24 16 24S32 26 32 16C32 7.163 24.837 0 16 0z" fill="url(#pinGrad)"/>
            <circle cx="16" cy="15" r="6" fill="rgba(255,255,255,0.25)"/>
            <circle cx="16" cy="15" r="3.5" fill="#FAF8F4"/>
          </svg>
          <div style="position:absolute;top:-4px;left:50%;transform:translateX(-50%);width:40px;height:40px;border-radius:50%;background:rgba(201,168,76,0.18);animation:landingPulse 2.2s ease-out infinite;pointer-events:none;"></div>
        </div>`,
        iconSize: [32, 40], iconAnchor: [16, 40],
      });

      const photoHtml = p.photo_url
        ? `<img src="${p.photo_url}" style="width:38px;height:38px;border-radius:10px;object-fit:cover;border:1.5px solid rgba(201,168,76,0.3);flex-shrink:0;" />`
        : `<div style="width:38px;height:38px;border-radius:10px;background:rgba(201,168,76,0.12);border:1.5px solid rgba(201,168,76,0.25);display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:16px;">🔧</div>`;

      const starsHtml = p.avg_rating > 0
        ? `<div style="display:flex;align-items:center;gap:4px;margin-bottom:10px;">
            ${"★".repeat(Math.round(p.avg_rating))}${"☆".repeat(5 - Math.round(p.avg_rating))}
            <span style="font-size:11px;color:#C9A84C;font-weight:700;margin-left:2px;">${p.avg_rating.toFixed(1)}</span>
           </div>`
        : `<div style="font-size:10px;color:rgba(201,168,76,0.5);margin-bottom:10px;letter-spacing:0.05em;">NEW LISTING</div>`;

      const marker = L.marker([p.lat, p.lng], { icon });
      marker.bindPopup(`
        <div style="font-family:DM Sans,sans-serif;background:linear-gradient(160deg,#1E1B18,#141210);border-radius:14px;padding:14px;width:210px;border:1px solid rgba(201,168,76,0.18);box-shadow:0 16px 48px rgba(0,0,0,0.7);">
          <div style="display:flex;gap:10px;align-items:flex-start;margin-bottom:10px;">
            ${photoHtml}
            <div style="flex:1;min-width:0;">
              <div style="font-family:Syne,sans-serif;font-size:14px;font-weight:800;color:#FAF8F4;line-height:1.2;margin-bottom:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${p.name}</div>
              <div style="font-size:10px;color:rgba(250,248,244,0.4);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${tradeName}</div>
            </div>
          </div>
          ${starsHtml}
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
            <div style="font-size:10px;color:rgba(250,248,244,0.3);display:flex;align-items:center;gap:3px;">
              <span>📍</span>${p.village ? p.village + ", " : ""}${p.region || ""}
            </div>
            ${p.rate_per_day ? `<div style="font-size:11px;color:#C9A84C;font-weight:800;font-family:Syne,sans-serif;">GYD ${Number(p.rate_per_day).toLocaleString()}</div>` : ""}
          </div>
          <button onclick="window.__hsSelectProvider('${p.id}')" style="background:linear-gradient(135deg,#C9A84C,#E4C76B);color:#0E0D0B;border:none;border-radius:9px;padding:9px 0;font-size:12px;font-weight:800;cursor:pointer;width:100%;font-family:Syne,sans-serif;letter-spacing:-0.01em;">View Full Profile →</button>
        </div>
      `, { className: "hs-map-popup", maxWidth: 240 });
      cluster.addLayer(marker);
    });
  }, [visibleProviders.length, filterTrade]);

  useEffect(() => {
    window.__hsSelectProvider = (id) => {
      const p = providers.find(x => String(x.id) === String(id));
      if (p) onSelect(p);
    };
    return () => { delete window.__hsSelectProvider; };
  }, [providers, onSelect]);

  const withPins = visibleProviders.filter(p => p.lat && p.lng).length;
  const isMobile = typeof window !== "undefined" && window.innerWidth < 641;

  return (
    <div style={{ marginBottom: 32, position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(250,248,244,0.3)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Live Tradesperson Map</div>
        {withPins > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.18)", borderRadius: 20, padding: "4px 12px" }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#C9A84C", boxShadow: "0 0 6px rgba(201,168,76,0.7)" }} />
            <span style={{ fontSize: 11, color: "#C9A84C", fontWeight: 700 }}>{withPins} on map</span>
          </div>
        )}
      </div>
      <div style={{ borderRadius: 16, overflow: "hidden", border: "1px solid rgba(201,168,76,0.15)", height: isMobile ? 280 : 420, position: "relative", boxShadow: "0 8px 40px rgba(0,0,0,0.4)" }}>
        <div id={mapId.current} style={{ width: "100%", height: "100%" }} />
        {withPins === 0 && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none", zIndex: 500 }}>
            <div style={{ background: "rgba(14,13,11,0.82)", backdropFilter: "blur(8px)", borderRadius: 12, padding: "14px 20px", textAlign: "center", border: "1px solid rgba(255,255,255,0.07)" }}>
              <div style={{ fontSize: 13, color: "rgba(250,248,244,0.45)", marginBottom: 4 }}>No pins yet</div>
              <div style={{ fontSize: 11, color: "rgba(250,248,244,0.25)" }}>Tradespeople appear here once they set their location</div>
            </div>
          </div>
        )}
        <div style={{ position: "absolute", bottom: 4, right: 6, fontSize: 9, color: "rgba(255,255,255,0.2)", zIndex: 500, pointerEvents: "none" }}>© OpenStreetMap · CartoDB</div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN — SKILLED TRADES PAGE
// ============================================================
export const SkilledTradesPage = ({ onBack, providerUser, onProviderAuth, onProviderLogout, openAuth = false, initialTradeFilter = "" }) => {
  const [providers, setProviders]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [search, setSearch]                 = useState("");
  const [filterTrade, setFilterTrade]       = useState(initialTradeFilter);
  const [filterRegion, setFilterRegion]     = useState("");
  const [activeTab, setActiveTab]           = useState("browse");
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showAuth, setShowAuth]             = useState(openAuth);  // tradesperson login/signup
  const [showDashboard, setShowDashboard]   = useState(false);
  const [sortBy, setSortBy]                 = useState("rating");  // "rating" | "price_asc" | "price_desc" | "newest"
  const [verifiedOnly, setVerifiedOnly]     = useState(false);

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("service_providers").select("*")
      .in("status", ["active", "verified"])
      .is("deleted_at", null)
      .order("avg_rating", { ascending: false });
    setProviders(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchProviders(); }, [fetchProviders]);

  // If a logged-in provider opens the page, take them straight to dashboard
  useEffect(() => {
    if (providerUser) setShowDashboard(true);
  }, [providerUser]);

  const filtered = providers
    .filter(p => {
      const q = search.toLowerCase();
      const pTrades = p.trades?.length ? p.trades : p.trade ? [p.trade] : [];
      const tradeLabels = pTrades.map(id => TRADES.find(t => t.id === id)?.label || "").join(" ").toLowerCase();
      const matchSearch   = !q || p.name?.toLowerCase().includes(q) || tradeLabels.includes(q);
      const matchTrade    = !filterTrade || pTrades.includes(filterTrade);
      const matchRegion   = !filterRegion || p.region === filterRegion;
      const matchVerified = !verifiedOnly || p.status === "verified";
      return matchSearch && matchTrade && matchRegion && matchVerified;
    })
    .sort((a, b) => {
      if (sortBy === "rating")     return (b.avg_rating || 0) - (a.avg_rating || 0);
      if (sortBy === "price_asc")  return (a.rate_per_day || 0) - (b.rate_per_day || 0);
      if (sortBy === "price_desc") return (b.rate_per_day || 0) - (a.rate_per_day || 0);
      if (sortBy === "newest")     return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      return 0;
    });

  const svcsCSS = `
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');

    .hirespot-root, .hirespot-root * { box-sizing: border-box; }
    .hirespot-root {
      --bg:      #0F0E0C;
      --bg2:     #1A1714;
      --bg3:     #221E18;
      --text:    #FAF8F4;
      --text2:   rgba(250,248,244,0.55);
      --text3:   rgba(250,248,244,0.28);
      --border:  rgba(255,255,255,0.06);
      --border2: rgba(255,255,255,0.09);
      --gold:    #C9A84C;
      --gold2:   #E0B95A;
      --red:     #D93025;
      --green:   #2D9E4E;
      font-family: 'DM Sans', sans-serif;
      color: #FAF8F4;
      background: #0F0E0C;
      min-height: 100vh;
    }
    .hirespot-root input, .hirespot-root select, .hirespot-root textarea { color-scheme: dark; background-color: #1A1714 !important; color: #FAF8F4 !important; }
    .hirespot-root input::placeholder, .hirespot-root textarea::placeholder { color: rgba(250,248,244,0.25); }
    .hirespot-root select option { background-color: #1A1714 !important; color: #FAF8F4 !important; }
    .hirespot-root select:focus, .hirespot-root input:focus, .hirespot-root textarea:focus { border-color: rgba(201,168,76,0.45) !important; box-shadow: 0 0 0 3px rgba(201,168,76,0.08) !important; }

    .svc-tab { padding: 13px 18px; font-size: 13px; font-weight: 600; background: transparent; border: none; border-bottom: 2px solid transparent; cursor: pointer; color: rgba(250,248,244,0.35); font-family: 'DM Sans',sans-serif; transition: all 0.18s; letter-spacing: 0.01em; }
    .svc-tab.active { color: #C9A84C; border-bottom-color: #C9A84C; }
    .svc-tab:hover:not(.active) { color: rgba(250,248,244,0.7); }
    .svc-trade-pill { border: 1px solid rgba(255,255,255,0.1); border-radius: 22px; padding: 8px 16px; font-size: 12px; cursor: pointer; transition: all 0.18s; background: rgba(255,255,255,0.03); color: rgba(250,248,244,0.5); font-family: 'DM Sans',sans-serif; white-space: nowrap; display: inline-flex; align-items: center; gap: 6px; }
    .svc-trade-pill.active { background: #C9A84C; border-color: #C9A84C; color: #0E0D0B; font-weight: 700; box-shadow: 0 0 18px rgba(201,168,76,0.25); }
    .svc-trade-pill:hover:not(.active) { background: rgba(255,255,255,0.06); color: rgba(250,248,244,0.8); border-color: rgba(255,255,255,0.18); }
    .svc-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(290px, 1fr)); gap: 14px; }
    .svc-pill-row::-webkit-scrollbar { display: none; }
    /* Desktop: wrap into two tidy rows */
    @media (min-width: 641px) { .svc-pill-row { flex-wrap: wrap !important; overflow: visible !important; } }
    @media (max-width: 640px) { .svc-grid { grid-template-columns: 1fr; gap: 12px; } .svc-trade-pill { font-size: 11px; padding: 6px 12px; } }

    /* Hero section */
    .hs-hero { position: relative; overflow: hidden; padding: 56px 0 48px; }
    .hs-hero-bg { position: absolute; inset: 0; background: radial-gradient(ellipse 70% 80% at 80% 50%, rgba(201,168,76,0.07) 0%, transparent 70%), radial-gradient(ellipse 50% 60% at 10% 80%, rgba(45,158,78,0.04) 0%, transparent 60%); pointer-events: none; }
    .hs-hero-grid { position: absolute; inset: 0; background-image: linear-gradient(rgba(255,255,255,0.012) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.012) 1px, transparent 1px); background-size: 40px 40px; pointer-events: none; }
    .hs-badge { display: inline-flex; align-items: center; gap: 7px; background: rgba(201,168,76,0.1); border: 1px solid rgba(201,168,76,0.22); border-radius: 22px; padding: 5px 14px; margin-bottom: 22px; }
    .hs-badge-dot { width: 7px; height: 7px; border-radius: 50%; background: #2D9E4E; box-shadow: 0 0 8px rgba(45,158,78,0.7); display: inline-block; }
    .hs-badge-text { font-size: 11px; font-weight: 700; color: #C9A84C; text-transform: uppercase; letter-spacing: 0.09em; font-family: 'DM Sans', sans-serif; }
    .hs-hero-title { font-family: 'Syne', sans-serif; font-size: clamp(30px, 5vw, 46px); font-weight: 800; color: #FAF8F4; line-height: 1.1; letter-spacing: -0.025em; margin-bottom: 18px; }
    .hs-hero-title .gold { background: linear-gradient(135deg, #C9A84C, #E8C96A, #C9A84C); background-size: 200%; -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
    .hs-hero-desc { font-size: 14px; color: rgba(250,248,244,0.45); line-height: 1.7; margin-bottom: 28px; max-width: 440px; }
    .hs-hero-btns { display: flex; gap: 12px; flex-wrap: wrap; }
    .hs-btn-solid { background: #FAF8F4; color: #0E0D0B; border: none; border-radius: 8px; padding: 13px 22px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: 'Syne', sans-serif; letter-spacing: -0.01em; transition: all 0.18s; }
    .hs-btn-solid:hover { background: #fff; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(250,248,244,0.15); }
    .hs-btn-outline { background: transparent; color: #FAF8F4; border: 1px solid rgba(250,248,244,0.25); border-radius: 8px; padding: 13px 22px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.18s; }
    .hs-btn-outline:hover { border-color: rgba(250,248,244,0.5); background: rgba(250,248,244,0.04); }

    /* Stats bar */
    .hs-stats-bar { border-top: 1px solid rgba(255,255,255,0.07); border-bottom: 1px solid rgba(255,255,255,0.07); display: flex; }
    .hs-stat { flex: 1; padding: 22px 0; text-align: center; border-right: 1px solid rgba(255,255,255,0.07); }
    .hs-stat:last-child { border-right: none; }
    .hs-stat-val { font-family: 'Syne', sans-serif; font-size: 26px; font-weight: 800; color: #FAF8F4; letter-spacing: -0.03em; line-height: 1; }
    .hs-stat-val .pct { color: #C9A84C; }
    .hs-stat-label { font-size: 10px; color: rgba(250,248,244,0.3); text-transform: uppercase; letter-spacing: 0.08em; margin-top: 5px; }
    @media (max-width: 500px) { .hs-stat-val { font-size: 20px; } .hs-stat-label { font-size: 9px; } .hs-stat { padding: 16px 4px; } }

    /* Featured label */
    .hs-section-label { font-size: 11px; font-weight: 700; color: rgba(250,248,244,0.3); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 16px; margin-top: 4px; }

    /* Bottom CTA banner */
    .hs-cta-banner { margin-top: 40px; background: rgba(201,168,76,0.05); border: 1px solid rgba(201,168,76,0.14); border-radius: 16px; padding: 22px 28px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
    .hs-cta-banner-text { font-size: 14px; color: rgba(250,248,244,0.55); line-height: 1.55; }
    .hs-cta-banner-text strong { color: #FAF8F4; font-weight: 700; }
    .hs-cta-btn { background: transparent; color: #FAF8F4; border: 1px solid rgba(250,248,244,0.25); border-radius: 8px; padding: 11px 20px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: 'DM Sans', sans-serif; white-space: nowrap; transition: all 0.18s; }
    .hs-cta-btn:hover { border-color: rgba(250,248,244,0.5); background: rgba(250,248,244,0.05); }

    /* Nav header buttons */
    .hs-nav-btn { background: transparent; color: #FAF8F4; border: 1px solid rgba(250,248,244,0.22); border-radius: 8px; padding: 8px 18px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.18s; }
    .hs-nav-btn:hover { border-color: rgba(250,248,244,0.45); background: rgba(250,248,244,0.04); }
    .hs-nav-btn.gold { background: linear-gradient(135deg,#C9A84C,#E4C76B); color: #0E0D0B; border: none; font-weight: 700; box-shadow: 0 2px 12px rgba(201,168,76,0.3); }
    .hs-nav-btn.gold:hover { transform: translateY(-1px); box-shadow: 0 4px 18px rgba(201,168,76,0.4); }

    @keyframes slideUp { from { transform: translateY(100%); opacity: 0.5; } to { transform: translateY(0); opacity: 1; } }
    @keyframes fadeUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    @keyframes pulse { 0%,100%{ opacity: 0.4; } 50%{ opacity: 0.7; } }
    @keyframes shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes landingPulse { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(2.8); opacity: 0; } }
    .svc-skeleton { background: linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 75%); background-size: 400px 100%; animation: shimmer 1.6s infinite; }

    /* ── Trade category grid ── */
    .hs-trade-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 40px; }
    @media (max-width: 640px) { .hs-trade-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; } }
    .hs-trade-tile {
      position: relative; border-radius: 16px; padding: 18px 16px 16px;
      background: linear-gradient(145deg, #1C1917, #141210);
      border: 1px solid rgba(255,255,255,0.07);
      cursor: pointer; transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1);
      overflow: hidden; display: flex; flex-direction: column; gap: 10px;
      -webkit-tap-highlight-color: transparent;
    }
    .hs-trade-tile::before {
      content: ""; position: absolute; inset: 0; border-radius: 16px;
      background: linear-gradient(135deg, rgba(201,168,76,0.08), transparent 60%);
      opacity: 0; transition: opacity 0.22s;
    }
    .hs-trade-tile:hover { transform: translateY(-4px) scale(1.01); border-color: rgba(201,168,76,0.35); box-shadow: 0 12px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(201,168,76,0.15); }
    .hs-trade-tile:hover::before { opacity: 1; }
    .hs-trade-tile.active { border-color: #C9A84C; background: linear-gradient(145deg,#221D14,#1A1610); box-shadow: 0 0 0 1px rgba(201,168,76,0.3), 0 8px 32px rgba(201,168,76,0.12); }
    .hs-trade-tile.active::before { opacity: 1; }
    .hs-trade-tile:active { transform: scale(0.97); }
    .hs-tile-icon { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; background: rgba(201,168,76,0.1); border: 1px solid rgba(201,168,76,0.18); flex-shrink: 0; transition: all 0.22s; }
    .hs-trade-tile:hover .hs-tile-icon, .hs-trade-tile.active .hs-tile-icon { background: rgba(201,168,76,0.18); border-color: rgba(201,168,76,0.35); }
    .hs-tile-name { font-family: 'Syne', sans-serif; font-size: 13px; font-weight: 700; color: #FAF8F4; line-height: 1.25; letter-spacing: -0.01em; }
    .hs-tile-count { font-size: 11px; color: rgba(250,248,244,0.35); font-weight: 500; display: flex; align-items: center; gap: 4px; }
    .hs-tile-count-dot { width: 5px; height: 5px; border-radius: 50%; background: #2D9E4E; box-shadow: 0 0 5px rgba(45,158,78,0.6); flex-shrink: 0; }
    .hs-tile-count-dot.zero { background: rgba(250,248,244,0.2); box-shadow: none; }
    .hs-tile-arrow { position: absolute; bottom: 14px; right: 14px; opacity: 0; transform: translateX(-4px); transition: all 0.2s; color: #C9A84C; }
    .hs-trade-tile:hover .hs-tile-arrow, .hs-trade-tile.active .hs-tile-arrow { opacity: 1; transform: translateX(0); }

    /* Leaflet popup — dark theme */
    .hs-map-popup .leaflet-popup-content-wrapper { background: transparent !important; border: none !important; box-shadow: 0 8px 32px rgba(0,0,0,0.6) !important; border-radius: 12px !important; padding: 0 !important; }
    .hs-map-popup .leaflet-popup-content { margin: 0 !important; }
    .hs-map-popup .leaflet-popup-tip-container { display: none !important; }
    .hs-map-popup .leaflet-popup-close-button { color: rgba(250,248,244,0.5) !important; top: 8px !important; right: 8px !important; font-size: 16px !important; }
    /* Cluster colour overrides */
    .marker-cluster-small, .marker-cluster-medium, .marker-cluster-large { background: rgba(201,168,76,0.12) !important; }
    .marker-cluster-small div, .marker-cluster-medium div, .marker-cluster-large div { background: rgba(201,168,76,0.22) !important; color: #C9A84C !important; font-family: Syne,sans-serif !important; font-weight: 800 !important; }
  `;

  // Show provider dashboard if logged in
  if (showDashboard && providerUser) {
    return (
      <>
        <style>{svcsCSS}</style>
        <ProviderDashboard
          provider={providerUser}
          onBack={() => setShowDashboard(false)}
          onLogout={() => { onProviderLogout?.(); setShowDashboard(false); }}
        />
        <SvcToast />
      </>
    );
  }

  return (
    <div className="hirespot-root" style={{ minHeight: "100vh", background: "#0F0E0C", paddingBottom: "calc(100px + env(safe-area-inset-bottom))" }}>
      <style>{svcsCSS}</style>

      {/* ── HEADER ── */}
      <div style={{ background: "#0F0E0C", borderBottom: "1px solid rgba(255,255,255,0.07)", position: "sticky", top: 0, zIndex: 80 }}>
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
            {/* Logo */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={onBack}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#C9A84C", boxShadow: "0 0 8px rgba(201,168,76,0.6)" }} />
              <div>
                <div style={{ fontFamily: "Syne,sans-serif", fontSize: 16, fontWeight: 800, color: "#FAF8F4", letterSpacing: "-0.01em", lineHeight: 1.1 }}>
                  Skilled Trades<span style={{ color: "#C9A84C" }}> GY</span>
                </div>
                <div style={{ fontSize: 10, color: "rgba(250,248,244,0.3)", marginTop: 1, letterSpacing: "0.02em" }}>Trusted tradespeople across Guyana</div>
              </div>
            </div>

            {/* Nav buttons */}
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button className="hs-nav-btn" onClick={() => { setFilterTrade(""); setSearch(""); }}>
                Browse all
              </button>
              {providerUser ? (
                <button className="hs-nav-btn gold" onClick={() => setShowDashboard(true)}>
                  My Dashboard
                </button>
              ) : (
                <button className="hs-nav-btn gold" onClick={() => setShowAuth(true)}>
                  List your trade
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 20px" }}>

        {/* ── HERO ── */}
        <div className="hs-hero">
          <div className="hs-hero-bg" />
          <div className="hs-hero-grid" />
          <div style={{ position: "relative" }}>
            <div className="hs-badge">
              <span className="hs-badge-dot" />
              <span className="hs-badge-text">Guyana's Trades Directory</span>
            </div>
            <h1 className="hs-hero-title">
              Find trusted <span className="gold">skilled<br />tradespeople</span> near you.
            </h1>
            <p className="hs-hero-desc">
              Browse verified plumbers, electricians, carpenters and more across all regions. Contact directly — no middleman.
            </p>
            <div className="hs-hero-btns">
              <button className="hs-btn-solid" onClick={() => { setFilterTrade(""); setSearch(""); }}>Browse tradespeople</button>
              <button className="hs-btn-outline" onClick={() => setShowAuth(true)}>List your trade — it's free</button>
            </div>
          </div>
        </div>

        {/* ── STATS BAR ── */}
        <div className="hs-stats-bar" style={{ marginBottom: 32 }}>
          <div className="hs-stat">
            <div className="hs-stat-val">{TRADES.length}<span className="pct" style={{ fontSize: 18 }}>+</span></div>
            <div className="hs-stat-label">Trade Categories</div>
          </div>
          <div className="hs-stat">
            <div className="hs-stat-val">{REGIONS.length}</div>
            <div className="hs-stat-label">Regions Covered</div>
          </div>
          <div className="hs-stat">
            <div className="hs-stat-val">100<span className="pct">%</span></div>
            <div className="hs-stat-label">Direct Contact</div>
          </div>
          <div className="hs-stat">
            <div className="hs-stat-val" style={{ fontFamily: "Syne,sans-serif" }}>Free</div>
            <div className="hs-stat-label">To Browse &amp; List</div>
          </div>
        </div>

        {/* ── LIVE MAP ── */}
        {!loading && (
          <LandingMap
            providers={providers}
            filterTrade={filterTrade}
            onSelect={(p) => setSelectedProvider(p)}
          />
        )}

        {/* ── BROWSE BY TRADE ── */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 16 }}>
            <div>
              <div style={{ fontFamily: "Syne,sans-serif", fontSize: 20, fontWeight: 800, color: "#FAF8F4", letterSpacing: "-0.02em", lineHeight: 1.2 }}>Browse by Trade</div>
              <div style={{ fontSize: 12, color: "rgba(250,248,244,0.35)", marginTop: 3 }}>Tap a category to filter tradespeople</div>
            </div>
            {filterTrade && (
              <button onClick={() => setFilterTrade("")}
                style={{ background: "none", border: "none", color: "rgba(201,168,76,0.7)", fontSize: 12, cursor: "pointer", fontFamily: "DM Sans,sans-serif", fontWeight: 600, padding: 0 }}>
                Clear ×
              </button>
            )}
          </div>
          <div className="hs-trade-grid">
            {TRADES.map(t => {
              const count = providers.filter(p => (p.trades?.length ? p.trades : p.trade ? [p.trade] : []).includes(t.id)).length;
              const isActive = filterTrade === t.id;
              return (
                <div key={t.id}
                  className={`hs-trade-tile${isActive ? " active" : ""}`}
                  onClick={() => { setFilterTrade(isActive ? "" : t.id); }}
                >
                  <div className="hs-tile-icon">
                    <TradeIcon id={t.id} size={20} color={isActive ? "#C9A84C" : "rgba(201,168,76,0.65)"} />
                  </div>
                  <div>
                    <div className="hs-tile-name">{t.label}</div>
                    <div className="hs-tile-count" style={{ marginTop: 4 }}>
                      <span className={`hs-tile-count-dot${count === 0 ? " zero" : ""}`} />
                      {count > 0 ? `${count} available` : "Be the first"}
                    </div>
                  </div>
                  <svg className="hs-tile-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── BROWSE TAB ── */}
        {activeTab === "browse" && (
          <>
            {/* Search + Region */}
            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "6px 8px" }}>
              <div style={{ flex: 1, minWidth: 200, position: "relative", display: "flex", alignItems: "center" }}>
                <svg style={{ position: "absolute", left: 14, pointerEvents: "none", flexShrink: 0 }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(250,248,244,0.3)" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or trade…"
                  style={{ width: "100%", background: "transparent", border: "none", padding: "11px 14px 11px 40px", color: "#FAF8F4", fontSize: 14, outline: "none", fontFamily: "DM Sans,sans-serif", boxSizing: "border-box" }} />
              </div>
              <div style={{ width: 1, background: "rgba(255,255,255,0.08)", alignSelf: "stretch", margin: "4px 0" }} />
              <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)}
                style={{ background: "transparent", border: "none", padding: "11px 14px", color: filterRegion ? "#FAF8F4" : "rgba(250,248,244,0.35)", fontSize: 14, outline: "none", fontFamily: "DM Sans,sans-serif", minWidth: 160, appearance: "none", cursor: "pointer", colorScheme: "dark" }}>
                <option value="">All Regions</option>
                {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            {/* ── SORT + FILTER STRIP ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, overflowX: "auto", overflowY: "visible", paddingBottom: 4, marginBottom: 24, scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch", flexWrap: "nowrap" }}>
              {/* Sort label */}
              <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(250,248,244,0.28)", textTransform: "uppercase", letterSpacing: "0.07em", flexShrink: 0, paddingRight: 2 }}>Sort</span>

              {[
                { key: "rating",     icon: "⭐", label: "Best Rated"  },
                { key: "price_asc",  icon: "↑",  label: "Price: Low"  },
                { key: "price_desc", icon: "↓",  label: "Price: High" },
                { key: "newest",     icon: "🕐", label: "Newest"      },
              ].map(({ key, icon, label }) => {
                const active = sortBy === key;
                return (
                  <button key={key} onClick={() => setSortBy(key)} style={{
                    flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "7px 13px", borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 500,
                    cursor: "pointer", fontFamily: "DM Sans,sans-serif", transition: "all 0.18s",
                    background: active ? "#C9A84C" : "rgba(255,255,255,0.05)",
                    border: `1px solid ${active ? "#C9A84C" : "rgba(255,255,255,0.09)"}`,
                    color: active ? "#0E0D0B" : "rgba(250,248,244,0.55)",
                    boxShadow: active ? "0 2px 12px rgba(201,168,76,0.3)" : "none",
                  }}>
                    <span style={{ fontSize: 11 }}>{icon}</span>{label}
                  </button>
                );
              })}

              {/* Divider */}
              <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.1)", flexShrink: 0, margin: "0 2px" }} />

              {/* Verified toggle */}
              <button onClick={() => setVerifiedOnly(v => !v)} style={{
                flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 5,
                padding: "7px 13px", borderRadius: 20, fontSize: 12, fontWeight: verifiedOnly ? 700 : 500,
                cursor: "pointer", fontFamily: "DM Sans,sans-serif", transition: "all 0.18s",
                background: verifiedOnly ? "rgba(45,158,78,0.15)" : "rgba(255,255,255,0.05)",
                border: `1px solid ${verifiedOnly ? "rgba(45,158,78,0.5)" : "rgba(255,255,255,0.09)"}`,
                color: verifiedOnly ? "#2D9E4E" : "rgba(250,248,244,0.55)",
                boxShadow: verifiedOnly ? "0 2px 12px rgba(45,158,78,0.15)" : "none",
              }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Verified
              </button>
            </div>

            <div className="hs-section-label">Featured Tradespeople</div>

            {loading ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(290px,1fr))", gap: 14 }}>
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} style={{ border: "1px solid rgba(255,255,255,0.06)", borderRadius: 18, height: 190, overflow: "hidden" }}>
                    <div className="svc-skeleton" style={{ height: 2 }} />
                    <div style={{ padding: "18px 18px 0" }}>
                      <div style={{ display: "flex", gap: 13, marginBottom: 12 }}>
                        <div className="svc-skeleton" style={{ width: 54, height: 54, borderRadius: 15, flexShrink: 0 }} />
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, paddingTop: 4 }}>
                          <div className="svc-skeleton" style={{ height: 14, borderRadius: 6, width: "70%" }} />
                          <div className="svc-skeleton" style={{ height: 11, borderRadius: 6, width: "45%" }} />
                          <div className="svc-skeleton" style={{ height: 10, borderRadius: 6, width: "55%" }} />
                        </div>
                      </div>
                      <div className="svc-skeleton" style={{ height: 10, borderRadius: 6, marginBottom: 6 }} />
                      <div className="svc-skeleton" style={{ height: 10, borderRadius: 6, width: "80%" }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "64px 20px" }}>
                <div style={{ width: 56, height: 56, borderRadius: 18, background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.15)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(201,168,76,0.5)" strokeWidth="1.8"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </div>
                <div style={{ fontFamily: "Syne,sans-serif", fontSize: 18, fontWeight: 800, color: "#FAF8F4", marginBottom: 8, letterSpacing: "-0.02em" }}>No tradespeople found</div>
                <div style={{ fontSize: 14, color: "rgba(250,248,244,0.35)", marginBottom: 28, lineHeight: 1.6 }}>Try adjusting your filters, or be the first to list in this category.</div>
                <button onClick={() => setShowAuth(true)}
                  style={{ background: "linear-gradient(135deg,#C9A84C,#E0B95A)", color: "#0E0D0B", border: "none", borderRadius: 12, padding: "13px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "Syne,sans-serif", boxShadow: "0 4px 20px rgba(201,168,76,0.3)" }}>
                  List Your Trade →
                </button>
              </div>
            ) : (
              <div className="svc-grid">
                {filtered.map(p => (
                  <ProviderCard key={p.id} provider={p} onClick={() => setSelectedProvider(p)} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── ARE YOU A TRADESMAN CTA ── */}
        {activeTab === "browse" && !loading && filtered.length > 0 && !providerUser && (
          <div className="hs-cta-banner">
            <p className="hs-cta-banner-text">
              <strong>Are you a tradesperson?</strong> Create your free listing and get contacted by customers directly via WhatsApp or phone. Takes 2 minutes.
            </p>
            <button className="hs-cta-btn" onClick={() => setShowAuth(true)}>
              List for free →
            </button>
          </div>
        )}
      </div>

      {/* ── MODALS ── */}
      {selectedProvider && (
        <ProviderModal provider={selectedProvider} onClose={() => { setSelectedProvider(null); fetchProviders(); }} />
      )}
      {showAuth && (
        <ProviderAuth
          onAuth={(provider) => { setShowAuth(false); onProviderAuth?.(provider); setShowDashboard(true); }}
          onBack={() => setShowAuth(false)}
        />
      )}
      <SvcToast />
    </div>
  );
};

export default SkilledTradesPage;

/*
================================================================================
  INTEGRATION GUIDE — What changed from ServicesPage.jsx
================================================================================

1. RENAMED EXPORTS:
   Old: export default ServicesPage
   New: export { SkilledTradesPage }   (also default exported)

   In App.jsx, change:
     import { ServicesPage } from "./ServicesPage";
   To:
     import { SkilledTradesPage } from "./SkilledTrades";

   And change the render:
     {screen === "services" && <ServicesPage onBack={...} />}
   To:
     {screen === "skills" && (
       <SkilledTradesPage
         onBack={() => setScreen("landing")}
         providerUser={providerUser}
         onProviderAuth={(p) => setProviderUser(p)}
         onProviderLogout={() => setProviderUser(null)}
       />
     )}

2. NEW STATE IN APP.JSX:
   Add alongside your existing state:
     const [providerUser, setProviderUser] = useState(null);

3. BOTTOM NAV — update the services tab label:
   Change "Services" → "Trades" in the nav button text.
   Change screen === "services" → screen === "skills" in activeTab logic.

4. SUPABASE — add 2 columns to service_providers table:
   ALTER TABLE service_providers
     ADD COLUMN email text,
     ADD COLUMN password_plain text;

   NOTE: password_plain is plaintext for MVP speed.
   When ready to upgrade, switch to Supabase Auth (supabase.auth.signUp)
   and store only the auth user UUID on the row.

5. SUPABASE — add lat/lng columns for map pin feature:
   ALTER TABLE service_providers
     ADD COLUMN lat double precision,
     ADD COLUMN lng double precision;

   Optional — create a spatial index for future distance queries:
   CREATE INDEX idx_service_providers_location
     ON service_providers (lat, lng)
     WHERE lat IS NOT NULL AND lng IS NOT NULL;

5. ADMIN WHATSAPP — replace the placeholder:
   const ADMIN_WHATSAPP = "5921234567";
   with your real number (digits only, with country code, no +).

================================================================================
*/
